import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SuperAdminAPI, SuperAdminB2BAPI } from '../lib/b2bApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MobileTabNav } from '../components/ui/mobile-tab-nav';
import NotificationBell from '../components/NotificationBell';
import { requestNotificationPermission, registerPushNotifications } from '../lib/pushNotifications';
import DownloadAppButton from '../components/DownloadAppButton';
import PushNotificationsPanel from '../components/PushNotificationsPanel';
import {
  Shield, LogOut, Users, Building2, ShoppingCart, IndianRupee,
  FlaskConical, Pill, Package, Clock, CheckCircle, XCircle,
  TrendingUp, AlertCircle, Search, Filter, User, UserCircle,
  Save, Edit2, Phone, Mail, MapPin, Image, Loader2, HelpCircle,
  Link, Link2, Plus, Trash2, Eye, Wallet, TrendingDown, Calendar,
  QrCode, Upload, CreditCard, BarChart3, Gift, Percent, Tag,
  FileText, Download, RefreshCw, Banknote, Settings, Star, LayoutGrid,
  AlertTriangle, Briefcase, Ban, MessageSquare, Send, MoreHorizontal, PauseCircle, PlayCircle, Zap, X, Video, Receipt, ArrowUpDown, Store
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { InvoiceUploader } from '../components/ReviewsAndInvoices';
import { paymentBadge } from '../lib/paymentStatus';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [pendingVendors, setPendingVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [marketplacePatients, setMarketplacePatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabRaw] = useState(searchParams.get('tab') || 'overview');
  const setActiveTab = useCallback((val) => {
    setActiveTabRaw(val);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'overview') next.delete('tab');
      else next.set('tab', val);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Filters
  const [patientSearch, setPatientSearch] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorStatusFilter, setVendorStatusFilter] = useState('all');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [bookingFilters, setBookingFilters] = useState({
    search: '', vendor_type: 'all', status: 'all', date_from: '', date_to: ''
  });
  
  // Admin Profile State
  const [adminProfile, setAdminProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Support Contacts State
  const [supportContacts, setSupportContacts] = useState({
    doctor_support: { support_email: '', support_phone: '', whatsapp_number: '', office_address: '', business_hours: '', additional_info: '' },
    vendor_support: { support_email: '', support_phone: '', whatsapp_number: '', office_address: '', business_hours: '', additional_info: '' }
  });
  const [savingSupport, setSavingSupport] = useState(false);
  
  // Vendor-Doctor Mappings State
  const [vendorDoctorMappings, setVendorDoctorMappings] = useState([]);
  const [mappingFilter, setMappingFilter] = useState({ status: 'all', vendor_type: 'all' });
  const [doctors, setDoctors] = useState([]);
  const [createMappingOpen, setCreateMappingOpen] = useState(false);
  const [newMapping, setNewMapping] = useState({ vendor_id: '', doctor_id: '', notes: '' });
  const [creatingMapping, setCreatingMapping] = useState(false);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [showNotifications, setShowNotifications] = useState(false);

  // Support Tickets State
  const [adminTickets, setAdminTickets] = useState([]);
  const [adminTicketStats, setAdminTicketStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, by_type: {} });
  const [ticketFilter, setTicketFilter] = useState({ status: 'all', user_type: 'all', category: 'all', search: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminReplyText, setAdminReplyText] = useState('');
  const [adminReplyStatus, setAdminReplyStatus] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Clinic Profitability State
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [profitabilityMonth, setProfitabilityMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loadingProfitability, setLoadingProfitability] = useState(false);
  const [profitabilitySearch, setProfitabilitySearch] = useState('');
  const [profitabilityDateRange, setProfitabilityDateRange] = useState({ start: '', end: '' });
  const [useProfitabilityDateRange, setUseProfitabilityDateRange] = useState(false);

  // Banner Ads State
  const [adminBanners, setAdminBanners] = useState([]);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerFileRef = useRef(null);
  const [bannerTitle, setBannerTitle] = useState('');

  // Payment QR Config State
  const [qrConfig, setQrConfig] = useState(null);
  const [qrForm, setQrForm] = useState({ upi_id: '', payee_name: '', qr_image: null });
  const [savingQr, setSavingQr] = useState(false);
  const [qrPreview, setQrPreview] = useState(null);
  const [pendingTopups, setPendingTopups] = useState([]);
  const [loadingTopups, setLoadingTopups] = useState(false);
  const [walletOverview, setWalletOverview] = useState(null);
  
  // Patient Top-up Requests State
  const [pendingPatientTopups, setPendingPatientTopups] = useState([]);
  const [patientTopupFilter, setPatientTopupFilter] = useState({ search: '', status: 'pending' });
  
  // Platform Config State (Vendor auto-approve toggle)
  const [platformConfig, setPlatformConfig] = useState({ vendor_auto_approve: false });
  const [savingPlatformConfig, setSavingPlatformConfig] = useState(false);
  
  // Vendor Wallet Top-up Requests & Redemption Requests
  const [vendorTopupRequests, setVendorTopupRequests] = useState([]);
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [redemptionFilter, setRedemptionFilter] = useState({ status: 'pending', user_type: 'all', search: '', date_from: '', date_to: '' });
  const [loadingPatientTopups, setLoadingPatientTopups] = useState(false);
  const [vendorWalletSearch, setVendorWalletSearch] = useState('');
  const [vendorWalletTypeFilter, setVendorWalletTypeFilter] = useState('all');
  const [topupFilter, setTopupFilter] = useState({ search: '', date_from: '', date_to: '' });

  // Portal Patients State
  const [portalPatients, setPortalPatients] = useState([]);
  const [portalPatientsTotal, setPortalPatientsTotal] = useState(0);
  const [portalPatientSearch, setPortalPatientSearch] = useState('');
  const [loadingPortalPatients, setLoadingPortalPatients] = useState(false);

  // Commission Dashboard State
  const [commissionData, setCommissionData] = useState(null);
  const [commissionFilters, setCommissionFilters] = useState({ vendor_type: '', date_from: '', date_to: '' });
  const [loadingCommission, setLoadingCommission] = useState(false);
  
  // Transactions Ledger State
  const [transactionsLedger, setTransactionsLedger] = useState([]);
  const [ledgerFilters, setLedgerFilters] = useState({ type: '', search: '', date_from: '', date_to: '' });
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [loadingLedger, setLoadingLedger] = useState(false);
  
  // Subscription Plans State
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '', tier: 'basic', billing_cycle: 'monthly', base_price: 0, description: '',
    features: {
      // Core features (default on for new plans)
      dashboard: true, appointments: true, patients: true, clinical: true, billing: true, 
      // Premium features (default off)
      marketplace: false, lab_access: false, pharma_access: false, inventory_access: false,
      inventory: false, ai_scribe: false, analytics: false, expenses: false, mr_visitors: false,
      patient_history: false, online_consultation: false, whatsapp_reminders: false, priority_support: false, custom_branding: false, face_vitals: false, health_tracking: false, marketing_growth: false, vendor_bills: false
    }
  });
  const [savingPlan, setSavingPlan] = useState(false);
  
  // All features list for admin UI - maps to doctor portal sidebar featureIds
  const allFeatures = [
    // Core features (now toggleable)
    { key: 'dashboard', label: 'Dashboard', category: 'core' },
    { key: 'appointments', label: 'Appointments', category: 'core' },
    { key: 'patients', label: 'Patients', category: 'core' },
    { key: 'clinical', label: 'Clinical', category: 'core' },
    { key: 'billing', label: 'Billing', category: 'core' },
    // Premium features (toggleable)
    { key: 'marketplace', label: 'B2B Marketplace', category: 'business' },
    { key: 'lab_access', label: 'Lab Vendor Access', category: 'business' },
    { key: 'pharma_access', label: 'Pharma Vendor Access', category: 'business' },
    { key: 'inventory_access', label: 'Inventory Vendor Access', category: 'business' },
    { key: 'inventory', label: 'Clinic Inventory', category: 'clinic' },
    { key: 'ai_scribe', label: 'AI Clinical Scribe', category: 'ai' },
    { key: 'analytics', label: 'Analytics', category: 'clinic' },
    { key: 'expenses', label: 'Expense Tracking', category: 'clinic' },
    { key: 'mr_visitors', label: 'MR Visitors', category: 'business' },
    { key: 'patient_history', label: 'Patient History', category: 'clinic' },
    { key: 'online_consultation', label: 'Online Video Consultation', category: 'clinic' },
    { key: 'face_vitals', label: 'Face Vitals Scanner', category: 'ai' },
    { key: 'health_tracking', label: 'Patient Health Tracking', category: 'ai' },
    { key: 'marketing_growth', label: 'Marketing & Growth', category: 'business' },
    { key: 'whatsapp_reminders', label: 'WhatsApp Reminders', category: 'communication' },
    { key: 'priority_support', label: 'Priority Support', category: 'support' },
    { key: 'custom_branding', label: 'Custom Branding', category: 'clinic' },
    { key: 'vendor_bills', label: 'Vendor Bills (B2B Billing Visibility)', category: 'business' }
  ];
  
  // Promo Codes State
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10, applicable_tiers: ['basic', 'advance', 'advance_plus'],
    max_uses: 0, valid_until: '', is_active: true
  });
  const [savingPromo, setSavingPromo] = useState(false);
  const [deletingPromoId, setDeletingPromoId] = useState(null);
  
  // Subscription Offers State
  const [offers, setOffers] = useState([]);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({
    title: '', description: '', offer_type: 'free_trial', duration_days: 30,
    discount_value: 0, tier: 'advance_plus', max_claims: 0
  });
  const [savingOffer, setSavingOffer] = useState(false);
  const [offerClaims, setOfferClaims] = useState([]);
  const [loadingOfferClaims, setLoadingOfferClaims] = useState(false);

  // Admin Referral Ledger State
  const [adminLedgerEntries, setAdminLedgerEntries] = useState([]);
  const [adminLedgerTotal, setAdminLedgerTotal] = useState(0);
  const [adminLedgerGrandTotal, setAdminLedgerGrandTotal] = useState(0);
  const [adminLedgerDoctorSummaries, setAdminLedgerDoctorSummaries] = useState([]);
  const [adminLedgerLoading, setAdminLedgerLoading] = useState(false);
  const [adminLedgerPage, setAdminLedgerPage] = useState(1);
  const [adminLedgerFilters, setAdminLedgerFilters] = useState({
    search: '', source_type: '', vendor_type: '', vendor_name: '', date_from: '', date_to: ''
  });

  // Vendor Billing (walk-in bills across all vendors) State
  const [vendorBills, setVendorBills] = useState([]);
  const [vendorBillsTotal, setVendorBillsTotal] = useState(0);
  const [vendorBillsSummary, setVendorBillsSummary] = useState({});
  const [vendorBillsLoading, setVendorBillsLoading] = useState(false);
  const [vendorBillsVendorTypes, setVendorBillsVendorTypes] = useState([]);
  const [selectedVendorBill, setSelectedVendorBill] = useState(null);
  const [vendorBillFilters, setVendorBillFilters] = useState({
    search: '', vendor_type: '', date_from: '', date_to: '', sort: 'created_at:-1'
  });
  
  // Doctor Subscriptions State
  const [doctorSubscriptions, setDoctorSubscriptions] = useState([]);
  const [doctorSubsLoading, setDoctorSubsLoading] = useState(false);
  const [doctorSubsFilters, setDoctorSubsFilters] = useState({
    subscription_status: '',
    tier: '',
    search: ''
  });
  const [doctorSubsPage, setDoctorSubsPage] = useState(1);
  const [doctorSubsTotal, setDoctorSubsTotal] = useState(0);
  
  // Doctor/Patient Edit & Delete State
  const [editDoctorDialog, setEditDoctorDialog] = useState(null);
  const [editDoctorForm, setEditDoctorForm] = useState({});
  const [editPatientDialog, setEditPatientDialog] = useState(null);
  const [editPatientForm, setEditPatientForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'doctor'|'patient'|'vendor', ids: [], names: [] }
  const [mpinDialogOpen, setMpinDialogOpen] = useState(false);
  const [deleteMpinValue, setDeleteMpinValue] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const [selectedClinicPatientIds, setSelectedClinicPatientIds] = useState([]);
  
  // Referral Config State
  const [referralConfig, setReferralConfig] = useState(null);
  const [referralForm, setReferralForm] = useState({ reward_type: 'flat', reward_value: 100, min_first_transaction: 100 });
  const [savingReferral, setSavingReferral] = useState(false);
  
  // Referrals List State
  const [referralsList, setReferralsList] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsFilters, setReferralsFilters] = useState({ status: '', user_type: '', search: '' });
  const [referralsPage, setReferralsPage] = useState(1);
  const [referralsTotal, setReferralsTotal] = useState(0);
  
  // Vendor Management State
  const [vendorDetails, setVendorDetails] = useState(null);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [vendorCommissionForm, setVendorCommissionForm] = useState({ commission_type: 'percentage', commission_value: 10 });
  const [savingVendorCommission, setSavingVendorCommission] = useState(false);
  const [vendorPayouts, setVendorPayouts] = useState([]);
  
  // Add Credits Dialog
  const [showAddCreditsDialog, setShowAddCreditsDialog] = useState(false);
  const [addCreditsForm, setAddCreditsForm] = useState({ doctor_id: '', amount: 0, reason: '' });
  const [addingCredits, setAddingCredits] = useState(false);
  const [doctorsList, setDoctorsList] = useState([]);

  // Doctor-Vendor Commission State
  const [doctorVendorCommissions, setDoctorVendorCommissions] = useState([]);
  const [doctorCommissionFilters, setDoctorCommissionFilters] = useState({ doctor_id: '', vendor_type: '', search: '' });
  const [loadingDoctorCommissions, setLoadingDoctorCommissions] = useState(false);
  const [showDoctorCommissionDialog, setShowDoctorCommissionDialog] = useState(false);
  const [doctorCommissionForm, setDoctorCommissionForm] = useState({ doctor_id: '', vendor_id: '', commission_percent: 10 });
  const [savingDoctorCommission, setSavingDoctorCommission] = useState(false);
  const [globalDefaultCommission, setGlobalDefaultCommission] = useState(10);
  const [allDoctorsList, setAllDoctorsList] = useState([]);
  const [commissionEarningsReport, setCommissionEarningsReport] = useState(null);

  // Patient Orders State (Admin View)
  const [adminPatientOrders, setAdminPatientOrders] = useState([]);
  const [adminPatientOrdersSummary, setAdminPatientOrdersSummary] = useState(null);
  const [adminPatientOrdersLoading, setAdminPatientOrdersLoading] = useState(false);
  const [adminPatientOrdersFilters, setAdminPatientOrdersFilters] = useState({ search: '', vendor_search: '', vendor_type: 'all', status: 'all', date_from: '', date_to: '' });
  const [adminPatientOrdersPage, setAdminPatientOrdersPage] = useState(1);
  const [adminPatientOrdersTotal, setAdminPatientOrdersTotal] = useState(0);

  // B2B Orders State (Admin View)
  const [adminB2BOrders, setAdminB2BOrders] = useState([]);
  const [adminB2BOrdersSummary, setAdminB2BOrdersSummary] = useState(null);
  const [adminB2BOrdersLoading, setAdminB2BOrdersLoading] = useState(false);
  const [adminB2BOrdersFilters, setAdminB2BOrdersFilters] = useState({ search: '', vendor_search: '', vendor_type: 'all', status: 'all', date_from: '', date_to: '' });
  const [adminB2BOrdersPage, setAdminB2BOrdersPage] = useState(1);
  const [adminB2BOrdersTotal, setAdminB2BOrdersTotal] = useState(0);
  const [adminChemistOrders, setAdminChemistOrders] = useState([]);
  const [adminChemistOrdersLoading, setAdminChemistOrdersLoading] = useState(false);

  // Credit Overview State
  const [creditOverview, setCreditOverview] = useState(null);

  // Consultation Coupons State
  const [consultCoupons, setConsultCoupons] = useState([]);
  const [consultCouponsLoading, setConsultCouponsLoading] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses_total: '', max_uses_per_patient: '', valid_from: '', valid_to: '', description: '', applicable_doctors: [] });
  const [couponDoctors, setCouponDoctors] = useState([]);
  const [couponDoctorScope, setCouponDoctorScope] = useState('all');

  // Admin Consultations State
  const [adminConsultations, setAdminConsultations] = useState([]);
  const [adminConsultLoading, setAdminConsultLoading] = useState(false);
  const [adminConsultFilters, setAdminConsultFilters] = useState({ search: '', status: 'all', has_coupon: 'all', date_from: '', date_to: '' });
  const [adminConsultSummary, setAdminConsultSummary] = useState(null);
  const [adminConsultPage, setAdminConsultPage] = useState(1);
  const [adminConsultTotal, setAdminConsultTotal] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('super_admin_token');
    const userData = localStorage.getItem('super_admin_user');
    if (!token) {
      navigate('/super-admin');
      return;
    }
    if (userData) setUser(JSON.parse(userData));
    fetchData();
    fetchPlatformConfig();
    fetchAdminBanners();
    // Register push notifications
    requestNotificationPermission().then(perm => {
      if (perm === 'granted') registerPushNotifications('super_admin');
    });

    // Auto-refresh every 30s
    const interval = setInterval(() => { if (!document.hidden) fetchData(); }, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [dashRes, pendingRes, vendorsRes, ordersRes, patientsRes] = await Promise.all([
        SuperAdminAPI.get('/dashboard'),
        SuperAdminB2BAPI.get('/vendors/pending'),
        SuperAdminB2BAPI.get('/vendors'),
        SuperAdminB2BAPI.get('/orders'),
        SuperAdminAPI.get('/patients').catch(() => ({ data: [] })),
      ]);
      setStats(dashRes.data);
      setPendingVendors(pendingRes.data);
      setAllVendors(vendorsRes.data);
      setAllOrders(ordersRes.data);
      setAllPatients(patientsRes.data || []);
      // Fetch credit overview
      const token = localStorage.getItem('super_admin_token');
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/credit/admin/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => setCreditOverview(r.data)).catch(() => {});
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorAction = async (vendorId, status, reason = '') => {
    try {
      await SuperAdminB2BAPI.put(`/vendors/${vendorId}/status`, { status, rejection_reason: reason });
      toast.success(`Vendor ${status}`);
      fetchData();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const confirmLogout = useCallback(() => {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin_user');
    setShowLogoutDialog(false);
    navigate('/super-admin');
    toast.success('Logged out successfully');
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    setShowLogoutDialog(true);
  }, []);

  const cancelLogout = useCallback(() => {
    setShowLogoutDialog(false);
  }, []);

  // Prevent back button from logging out - push state to history
  useEffect(() => {
    const token = localStorage.getItem('super_admin_token');
    if (!token) return;

    const handlePopState = () => {
      // If user presses back and would go to login page, prevent it
      if (window.location.pathname.includes('/super-admin/dashboard')) {
        // Push current state back to prevent leaving the app
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Initial push to prevent back from going to login
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Notification Functions
  const markNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setNotifications(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => n.id === notificationId ? { ...n, read: true } : n),
        unread_count: Math.max(0, prev.unread_count - 1)
      }));
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => ({ ...n, read: true })),
        unread_count: 0
      }));
    } catch (err) {
      console.error('Failed to mark all notifications read', err);
    }
  };

  // Support Tickets Functions
  const fetchAdminTickets = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (ticketFilter.status !== 'all') params.set('status', ticketFilter.status);
      if (ticketFilter.user_type !== 'all') params.set('user_type', ticketFilter.user_type);
      if (ticketFilter.category !== 'all') params.set('category', ticketFilter.category);
      if (ticketFilter.search) params.set('search', ticketFilter.search);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/support/admin/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminTickets(res.data.tickets || []);
      setAdminTicketStats(res.data.stats || {});
    } catch (e) { console.error('Fetch tickets error:', e); }
  };

  const handleAdminReply = async (ticketId) => {
    if (!adminReplyText.trim()) return;
    try {
      setSendingReply(true);
      const token = localStorage.getItem('super_admin_token');
      const body = { message: adminReplyText };
      if (adminReplyStatus) body.new_status = adminReplyStatus;
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/support/admin/tickets/${ticketId}/reply`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(res.data);
      setAdminReplyText('');
      setAdminReplyStatus('');
      fetchAdminTickets();
      toast.success('Reply sent');
    } catch (e) { toast.error('Failed to reply'); } finally { setSendingReply(false); }
  };

  // Admin Profile Functions
  const fetchAdminProfile = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const [profileRes, supportRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support-contacts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAdminProfile(profileRes.data);
      setProfileForm({ name: profileRes.data.name || '', email: profileRes.data.email || '', phone: profileRes.data.phone || '' });
      setSupportContacts(supportRes.data);
    } catch (err) {
      console.error('Failed to fetch admin profile', err);
    }
  };

  const saveAdminProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminProfile(res.data);
      setEditingProfile(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSupportContacts = async () => {
    setSavingSupport(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support-contacts`, supportContacts, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportContacts(res.data);
      toast.success('Support contacts updated successfully');
    } catch (err) {
      toast.error('Failed to update support contacts');
    } finally {
      setSavingSupport(false);
    }
  };

  const uploadAdminLogo = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setAdminProfile({ ...adminProfile, logo_url: res.data.url });
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  // Vendor-Doctor Mappings Functions
  const fetchVendorDoctorMappings = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const [mappingsRes, doctorsRes, notifRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/super-admin/patients?limit=500`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { notifications: [], unread_count: 0 } }))
      ]);
      setVendorDoctorMappings(mappingsRes.data);
      // Get unique doctors from users
      const doctorsList = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/clinics`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setDoctors(doctorsList.data || []);
      setNotifications(notifRes.data);
    } catch (err) {
      console.error('Failed to fetch mappings', err);
    }
  };

  // Clinic Profitability Functions
  const fetchClinicProfitability = async (options = {}) => {
    setLoadingProfitability(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/expenses/admin/clinic-profitability`;
      const params = new URLSearchParams();
      
      if (options.useDateRange && options.startDate && options.endDate) {
        params.append('start_date', options.startDate);
        params.append('end_date', options.endDate);
      } else {
        params.append('month', options.month || profitabilityMonth);
      }
      
      if (options.search) {
        params.append('search', options.search);
      }
      
      url += `?${params.toString()}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfitabilityData(res.data);
    } catch (err) {
      console.error('Failed to fetch profitability', err);
      toast.error('Failed to fetch clinic profitability data');
    } finally {
      setLoadingProfitability(false);
    }
  };

  const handleProfitabilitySearch = () => {
    fetchClinicProfitability({
      useDateRange: useProfitabilityDateRange,
      startDate: profitabilityDateRange.start,
      endDate: profitabilityDateRange.end,
      month: profitabilityMonth,
      search: profitabilitySearch
    });
  };

  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const createVendorDoctorMapping = async () => {
    if (!newMapping.vendor_id || !newMapping.doctor_id) {
      toast.error('Please select both vendor and doctor');
      return;
    }
    setCreatingMapping(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/admin/create`, newMapping, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping created successfully');
      setCreateMappingOpen(false);
      setNewMapping({ vendor_id: '', doctor_id: '', notes: '' });
      fetchVendorDoctorMappings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create mapping');
    } finally {
      setCreatingMapping(false);
    }
  };

  const approveMapping = async (mappingId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/admin/${mappingId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping approved');
      fetchVendorDoctorMappings();
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const rejectMapping = async (mappingId, reason = '') => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/admin/${mappingId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping rejected');
      fetchVendorDoctorMappings();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const deleteMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/admin/${mappingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping deleted');
      fetchVendorDoctorMappings();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // ============= Payment QR Config Functions =============
  const fetchPaymentSettings = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const [qrRes, walletRes, topupsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/qr-config`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/topup-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (qrRes.data.has_config) {
        setQrConfig(qrRes.data.config);
        setQrForm({
          upi_id: qrRes.data.config.upi_id || '',
          payee_name: qrRes.data.config.payee_name || '',
          qr_image: null
        });
        setQrPreview(qrRes.data.config.qr_image_url);
      }
      setWalletOverview(walletRes.data);
      setPendingTopups(topupsRes.data.requests || []);
    } catch (err) {
      console.error('Failed to fetch payment settings', err);
    }
  };

  const handleQrImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQrForm({ ...qrForm, qr_image: file });
      setQrPreview(URL.createObjectURL(file));
    }
  };

  const saveQrConfig = async () => {
    if (!qrForm.upi_id || !qrForm.payee_name) {
      toast.error('Please fill UPI ID and Payee Name');
      return;
    }
    setSavingQr(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const formData = new FormData();
      formData.append('upi_id', qrForm.upi_id);
      formData.append('payee_name', qrForm.payee_name);
      if (qrForm.qr_image) {
        formData.append('qr_image', qrForm.qr_image);
      }
      
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/qr-config`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Payment QR Config saved');
      fetchPaymentSettings();
    } catch (err) {
      toast.error('Failed to save QR config');
    } finally {
      setSavingQr(false);
    }
  };

  const handleTopupAction = async (requestId, action) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/topup-requests/${requestId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Top-up ${action}d successfully`);
      fetchPaymentSettings();
    } catch (err) {
      toast.error(`Failed to ${action} top-up`);
    }
  };

  // ============= Patient Top-up Functions =============
  const fetchPatientTopups = async () => {
    setLoadingPatientTopups(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (patientTopupFilter.status && patientTopupFilter.status !== 'all') params.append('status', patientTopupFilter.status);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/patient-wallet/admin/topup-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPatientTopups(res.data.requests || []);
    } catch (err) {
      console.error('Failed to fetch patient topups', err);
    } finally {
      setLoadingPatientTopups(false);
    }
  };

  const handlePatientTopupAction = async (requestId, action) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/patient-wallet/admin/topup-requests/${requestId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Patient top-up ${action}d successfully`);
      fetchPatientTopups();
    } catch (err) {
      toast.error(`Failed to ${action} patient top-up`);
    }
  };

  // ============= Portal Patients Functions =============
  const fetchPortalPatients = async () => {
    setLoadingPortalPatients(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (portalPatientSearch) params.append('search', portalPatientSearch);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/portal-patients?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortalPatients(res.data.patients || []);
      setPortalPatientsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch portal patients', err);
    } finally {
      setLoadingPortalPatients(false);
    }
  };

  const handlePortalPatientStatus = async (patientId, newStatus) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/portal-patients/${patientId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Patient ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
      fetchPortalPatients();
    } catch (err) {
      toast.error('Failed to update patient status');
    }
  };

  // Doctor subscription actions
  const handleDoctorSubAction = async (doctorId, action) => {
    const token = localStorage.getItem('super_admin_token');
    try {
      if (action === 'suspend') {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/doctors/${doctorId}/suspend-subscription`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Subscription suspended');
      } else if (action === 'reactivate') {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/doctors/${doctorId}/reactivate-subscription`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Subscription reactivated');
      }
      fetchDoctorSubscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const proceedingToStep2Ref = useRef(false);

  const handleDeleteAccount = () => {
    // Step 1 confirmed -> move to step 2 (MPIN re-entry). Guard against AlertDialogAction's
    // built-in auto-close firing onOpenChange(false) and wiping deleteConfirm before step 2 opens.
    if (!deleteConfirm) return;
    proceedingToStep2Ref.current = true;
    setDeleteMpinValue('');
    setMpinDialogOpen(true);
  };

  const confirmDeleteWithMpin = async () => {
    if (!deleteConfirm) return;
    if (!/^\d{4}$/.test(deleteMpinValue)) { toast.error('Enter your 4-digit MPIN'); return; }
    const token = localStorage.getItem('super_admin_token');
    const { type, ids } = deleteConfirm;
    const isBulk = ids.length > 1;
    setDeletingAccount(true);
    try {
      if (type === 'doctor') {
        if (isBulk) {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/doctors/bulk-delete`, { ids, mpin: deleteMpinValue }, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/doctors/${ids[0]}/account`, { data: { mpin: deleteMpinValue }, headers: { Authorization: `Bearer ${token}` } });
        }
        toast.success(isBulk ? `${ids.length} doctor accounts deleted` : 'Doctor account deleted');
        setSelectedDoctorIds([]);
        fetchDoctorSubscriptions();
      } else if (type === 'vendor') {
        if (isBulk) {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/vendors/bulk-delete`, { ids, mpin: deleteMpinValue }, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/vendors/${ids[0]}`, { data: { mpin: deleteMpinValue }, headers: { Authorization: `Bearer ${token}` } });
        }
        toast.success(isBulk ? `${ids.length} vendors deleted` : 'Vendor deleted');
        setSelectedVendorIds([]);
        fetchData();
      } else if (type === 'clinic-patient') {
        if (isBulk) {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/clinic-patients/bulk-delete`, { ids, mpin: deleteMpinValue }, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/clinic-patients/${ids[0]}`, { data: { mpin: deleteMpinValue }, headers: { Authorization: `Bearer ${token}` } });
        }
        toast.success(isBulk ? `${ids.length} patients deleted` : 'Patient deleted');
        setSelectedClinicPatientIds([]);
        fetchData();
      } else {
        if (isBulk) {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/portal-patients/bulk-delete`, { ids, mpin: deleteMpinValue }, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/portal-patients/${ids[0]}`, { data: { mpin: deleteMpinValue }, headers: { Authorization: `Bearer ${token}` } });
        }
        toast.success(isBulk ? `${ids.length} patient accounts deleted` : 'Patient account deleted');
        setSelectedPatientIds([]);
        fetchPortalPatients();
      }
      setMpinDialogOpen(false);
      setDeleteConfirm(null);
      setDeleteMpinValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleEditDoctorSave = async () => {
    const token = localStorage.getItem('super_admin_token');
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/doctors/${editDoctorDialog.id || editDoctorDialog.doctor_id}/profile`, editDoctorForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Doctor profile updated');
      setEditDoctorDialog(null);
      fetchDoctorSubscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleEditPatientSave = async () => {
    const token = localStorage.getItem('super_admin_token');
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/portal-patients/${editPatientDialog.id}`, editPatientForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Patient profile updated');
      setEditPatientDialog(null);
      fetchPortalPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  // ============= Commission Dashboard Functions =============
  const fetchCommissionData = async () => {
    setLoadingCommission(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (commissionFilters.vendor_type) params.append('vendor_type', commissionFilters.vendor_type);
      if (commissionFilters.date_from) params.append('date_from', commissionFilters.date_from);
      if (commissionFilters.date_to) params.append('date_to', commissionFilters.date_to);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/commission-dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissionData(res.data);
    } catch (err) {
      console.error('Failed to fetch commission data', err);
    } finally {
      setLoadingCommission(false);
    }
  };

  // ============= Transactions Ledger Functions =============
  const fetchTransactionsLedger = async (page = 1) => {
    setLoadingLedger(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams({ page, limit: 30 });
      if (ledgerFilters.type) params.append('type', ledgerFilters.type);
      if (ledgerFilters.search) params.append('search', ledgerFilters.search);
      if (ledgerFilters.date_from) params.append('date_from', ledgerFilters.date_from);
      if (ledgerFilters.date_to) params.append('date_to', ledgerFilters.date_to);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/topup-history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactionsLedger(res.data.requests || []);
      setLedgerTotal(res.data.total || 0);
      setLedgerPage(page);
    } catch (err) {
      console.error('Failed to fetch ledger', err);
    } finally {
      setLoadingLedger(false);
    }
  };

  // ============= Subscription Plans Functions =============
  const fetchSubscriptionPlans = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const [plansRes, promosRes, refConfigRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans?is_active=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/promo-codes`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { codes: [] } })),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/config`).catch(() => ({ data: { has_config: false } }))
      ]);
      setSubscriptionPlans(plansRes.data.plans || []);
      setPromoCodes(promosRes.data.promo_codes || promosRes.data.codes || promosRes.data || []);
      if (refConfigRes.data.has_config) {
        setReferralConfig(refConfigRes.data.config);
        setReferralForm({
          reward_type: refConfigRes.data.config.reward_type || 'flat',
          reward_value: refConfigRes.data.config.reward_value || 100,
          min_first_transaction: refConfigRes.data.config.min_first_transaction || 100
        });
      }
    } catch (err) {
      console.error('Failed to fetch subscription data', err);
    }
  };

  const savePlan = async () => {
    if (!planForm.name || !planForm.base_price) {
      toast.error('Please fill plan name and price');
      return;
    }
    setSavingPlan(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      if (editingPlan) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans/${editingPlan.id}`, planForm, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Plan updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans`, planForm, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Plan created');
      }
      setShowPlanDialog(false);
      setEditingPlan(null);
      setPlanForm({ name: '', tier: 'basic', billing_cycle: 'monthly', base_price: 0, description: '', features: {} });
      fetchSubscriptionPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const savePromoCode = async () => {
    if (!promoForm.code || !promoForm.discount_value) {
      toast.error('Please fill code and discount value');
      return;
    }
    setSavingPromo(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const payload = {
        ...promoForm,
        valid_until: promoForm.valid_until || null
      };
      
      if (editingPromo) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/promo-codes/${editingPromo.id}`, null, {
          params: {
            code: promoForm.code,
            discount_type: promoForm.discount_type,
            discount_value: promoForm.discount_value,
            max_uses: promoForm.max_uses,
            is_active: promoForm.is_active,
            valid_until: promoForm.valid_until || null,
            applicable_tiers: promoForm.applicable_tiers
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promo code updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/promo-codes`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Promo code created');
      }
      setShowPromoDialog(false);
      setEditingPromo(null);
      setPromoForm({ code: '', discount_type: 'percentage', discount_value: 10, applicable_tiers: ['basic', 'advance', 'advance_plus'], max_uses: 0, valid_until: '', is_active: true });
      fetchSubscriptionPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save promo code');
    } finally {
      setSavingPromo(false);
    }
  };

  const deletePromoCode = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    setDeletingPromoId(promoId);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/promo-codes/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promo code deleted');
      fetchSubscriptionPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete promo code');
    } finally {
      setDeletingPromoId(null);
    }
  };

  // ===== Subscription Offers =====
  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/offers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(res.data.offers || []);
    } catch (err) { console.error(err); }
  };

  const fetchOfferClaims = async () => {
    setLoadingOfferClaims(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/offer-claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOfferClaims(res.data.claims || []);
    } catch (err) { console.error(err); } finally { setLoadingOfferClaims(false); }
  };

  const saveOffer = async () => {
    if (!offerForm.title) { toast.error('Title is required'); return; }
    setSavingOffer(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      if (editingOffer) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/offers/${editingOffer.id}`, offerForm, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Offer updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/offers`, offerForm, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success('Offer created');
      }
      setShowOfferDialog(false);
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save offer');
    } finally { setSavingOffer(false); }
  };

  const deleteOffer = async (offerId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Offer deleted');
      fetchOffers();
    } catch (err) { toast.error('Failed to delete offer'); }
  };

  const toggleOfferActive = async (offer) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/offers/${offer.id}`, 
        { is_active: !offer.is_active },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      fetchOffers();
    } catch { toast.error('Failed to toggle offer'); }
  };

  // ===== Admin Banners =====
  const fetchAdminBanners = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/banners/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminBanners(res.data.banners || []);
    } catch (err) { console.error(err); }
  };

  const uploadAdminBanner = async (file) => {
    if (!file) return;
    setUploadingBanner(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', bannerTitle);
      formData.append('sort_order', adminBanners.length);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/banners/admin`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Banner uploaded');
      setBannerTitle('');
      fetchAdminBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload banner');
    } finally { setUploadingBanner(false); }
  };

  const deleteAdminBanner = async (bannerId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/banners/admin/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      fetchAdminBanners();
    } catch { toast.error('Failed to delete banner'); }
  };

  const toggleBannerActive = async (banner) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/banners/admin/${banner.id}`,
        { is_active: !banner.is_active },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      fetchAdminBanners();
    } catch { toast.error('Failed to update'); }
  };

  const fetchDoctorSubscriptions = async () => {
    setDoctorSubsLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (doctorSubsFilters.subscription_status) params.append('subscription_status', doctorSubsFilters.subscription_status);
      if (doctorSubsFilters.tier) params.append('tier', doctorSubsFilters.tier);
      if (doctorSubsFilters.search) params.append('search', doctorSubsFilters.search);
      params.append('page', doctorSubsPage);
      params.append('limit', 20);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/admin/doctors?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoctorSubscriptions(res.data.doctors || []);
      setDoctorSubsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch doctor subscriptions', err);
    } finally {
      setDoctorSubsLoading(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchDoctorSubscriptions();
    }
  }, [doctorSubsFilters, doctorSubsPage, activeTab]);

  const saveReferralConfig = async () => {
    setSavingReferral(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/config`, null, {
        params: referralForm,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referral config saved');
      fetchSubscriptionPlans();
    } catch (err) {
      toast.error('Failed to save referral config');
    } finally {
      setSavingReferral(false);
    }
  };

  // Fetch Referrals List
  const fetchReferralsList = async () => {
    setReferralsLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (referralsFilters.status) params.append('status', referralsFilters.status);
      if (referralsFilters.user_type) params.append('user_type', referralsFilters.user_type);
      if (referralsFilters.search) params.append('search', referralsFilters.search);
      params.append('page', referralsPage);
      params.append('limit', 20);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/admin/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralsList(res.data.referrals || []);
      setReferralsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch referrals', err);
    } finally {
      setReferralsLoading(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchReferralsList();
    }
  }, [referralsFilters, referralsPage, activeTab]);

  // Re-fetch redemption requests when filter changes
  useEffect(() => {
    if (activeTab === 'payments') {
      fetchRedemptionRequests();
    }
  }, [redemptionFilter]);

  // ============= Vendor Commission Management =============
  const fetchVendorDetails = async (vendorId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/admin/vendor/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorDetails(res.data);
      setVendorCommissionForm({
        commission_type: res.data.commission?.type || 'percentage',
        commission_value: res.data.commission?.value || 10
      });
      setShowVendorDialog(true);
    } catch (err) {
      toast.error('Failed to fetch vendor details');
    }
  };

  const saveVendorCommission = async () => {
    if (!vendorDetails) return;
    setSavingVendorCommission(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/config`, {
        vendor_id: vendorDetails.vendor.id,
        vendor_type: vendorDetails.vendor.vendor_type,
        ...vendorCommissionForm,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Referral charges config saved');
      setShowVendorDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save referral config');
    } finally {
      setSavingVendorCommission(false);
    }
  };

  const fetchVendorPayouts = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/admin/payout-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorPayouts(res.data.payouts || []);
    } catch (err) {
      console.error('Failed to fetch payouts', err);
    }
  };

  const handlePayoutAction = async (payoutId, action, transactionRef = '') => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/admin/payout/${payoutId}/${action}`, null, {
        params: { transaction_ref: transactionRef },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payout ${action}d`);
      fetchVendorPayouts();
      fetchPaymentSettings();
    } catch (err) {
      toast.error(`Failed to ${action} payout`);
    }
  };

  // ============= Add Credits to Doctor =============
  
  // Platform Config Functions
  const fetchPlatformConfig = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/platform-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlatformConfig(res.data);
    } catch (err) { console.error('Failed to fetch platform config', err); }
  };
  
  const toggleVendorAutoApprove = async (value) => {
    setSavingPlatformConfig(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/platform-config`, 
        { vendor_auto_approve: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatformConfig(prev => ({ ...prev, vendor_auto_approve: value }));
      toast.success(value ? 'Vendor auto-approve enabled' : 'Vendor auto-approve disabled');
    } catch (err) {
      toast.error('Failed to update setting');
    } finally {
      setSavingPlatformConfig(false);
    }
  };
  
  // Vendor Wallet Top-up Requests
  const fetchVendorTopupRequests = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-wallet/admin/topup-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorTopupRequests(res.data.requests || []);
    } catch (err) { console.error('Failed to fetch vendor topup requests', err); }
  };
  
  // Vendor Wallets Overview
  const [vendorWalletsList, setVendorWalletsList] = useState([]);
  const fetchVendorWallets = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-wallet/admin/all-wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorWalletsList(res.data.wallets || []);
    } catch (err) { console.error('Failed to fetch vendor wallets', err); }
  };
  
  const handleVendorTopupAction = async (requestId, action) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-wallet/admin/topup-requests/${requestId}/${action}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vendor top-up ${action}d`);
      fetchVendorTopupRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} top-up`);
    }
  };
  
  // Redemption Requests (all user types)
  const fetchRedemptionRequests = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (redemptionFilter.status) params.append('status', redemptionFilter.status);
      if (redemptionFilter.user_type && redemptionFilter.user_type !== 'all') params.append('user_type', redemptionFilter.user_type);
      if (redemptionFilter.search) params.append('search', redemptionFilter.search);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet-redemption/admin/requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedemptionRequests(res.data.requests || []);
    } catch (err) { console.error('Failed to fetch redemption requests', err); }
  };
  
  const handleRedemptionAction = async (requestId, action) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wallet-redemption/admin/requests/${requestId}/${action}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Redemption ${action}d`);
      fetchRedemptionRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} redemption`);
    }
  };

  const fetchDoctorsList = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/all-wallets?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoctorsList(res.data.wallets || []);
    } catch (err) {
      console.error('Failed to fetch doctors', err);
    }
  };

  const addCreditsToDoctor = async () => {
    if (!addCreditsForm.doctor_id || !addCreditsForm.amount || addCreditsForm.amount <= 0) {
      toast.error('Please select doctor and enter valid amount');
      return;
    }
    setAddingCredits(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/admin/direct-topup`, {
        doctor_id: addCreditsForm.doctor_id,
        amount: parseFloat(addCreditsForm.amount),
        reason: addCreditsForm.reason || 'Admin credit'
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Credits added successfully');
      setShowAddCreditsDialog(false);
      setAddCreditsForm({ doctor_id: '', amount: 0, reason: '' });
      fetchPaymentSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add credits');
    } finally {
      setAddingCredits(false);
    }
  };

  // ============= Admin Referral Ledger =============
  const fetchAdminReferralLedger = async (page = 1) => {
    setAdminLedgerLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '30');
      if (adminLedgerFilters.search) params.append('search', adminLedgerFilters.search);
      if (adminLedgerFilters.source_type) params.append('source_type', adminLedgerFilters.source_type);
      if (adminLedgerFilters.vendor_type) params.append('vendor_type', adminLedgerFilters.vendor_type);
      if (adminLedgerFilters.vendor_name) params.append('vendor_name', adminLedgerFilters.vendor_name);
      if (adminLedgerFilters.date_from) params.append('date_from', adminLedgerFilters.date_from);
      if (adminLedgerFilters.date_to) params.append('date_to', adminLedgerFilters.date_to);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/referral-ledger/admin?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminLedgerEntries(res.data.entries || []);
      setAdminLedgerTotal(res.data.total || 0);
      setAdminLedgerGrandTotal(res.data.grand_total || 0);
      setAdminLedgerDoctorSummaries(res.data.doctor_summaries || []);
      setAdminLedgerPage(page);
    } catch (err) { console.error('Failed to fetch referral ledger:', err); }
    finally { setAdminLedgerLoading(false); }
  };

  // ============= Vendor Billing (all vendors, walk-in bills) =============
  const fetchVendorBills = async (page = 1) => {
    setVendorBillsLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const [sort_by, sort_order] = vendorBillFilters.sort.split(':');
      const params = new URLSearchParams({ page, limit: '30', sort_by, sort_order });
      if (vendorBillFilters.search) params.append('search', vendorBillFilters.search);
      if (vendorBillFilters.vendor_type) params.append('vendor_type', vendorBillFilters.vendor_type);
      if (vendorBillFilters.date_from) params.append('date_from', vendorBillFilters.date_from);
      if (vendorBillFilters.date_to) params.append('date_to', vendorBillFilters.date_to);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chemist/admin/all-bills?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorBills(res.data.bills || []);
      setVendorBillsTotal(res.data.total || 0);
      setVendorBillsSummary(res.data.summary || {});
      setVendorBillsVendorTypes(res.data.vendor_types || []);
    } catch (err) { console.error('Failed to fetch vendor bills:', err); toast.error('Failed to load vendor billing ledger'); }
    finally { setVendorBillsLoading(false); }
  };

  // ============= Doctor-Vendor Commission Management =============
  const fetchDoctorVendorCommissions = async () => {
    setLoadingDoctorCommissions(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      if (doctorCommissionFilters.doctor_id) params.append('doctor_id', doctorCommissionFilters.doctor_id);
      if (doctorCommissionFilters.vendor_type) params.append('vendor_type', doctorCommissionFilters.vendor_type);
      if (doctorCommissionFilters.search) params.append('search', doctorCommissionFilters.search);
      
      const [commissionsRes, globalRes, doctorsRes, reportRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/doctor-vendor?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/global-default`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/clinics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/earnings-report`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setDoctorVendorCommissions(commissionsRes.data.configs || []);
      setGlobalDefaultCommission(globalRes.data.default_commission_percent || 10);
      setAllDoctorsList(doctorsRes.data || []);
      setCommissionEarningsReport(reportRes.data);
    } catch (err) {
      console.error('Failed to fetch doctor commissions', err);
      toast.error('Failed to fetch referral charges data');
    } finally {
      setLoadingDoctorCommissions(false);
    }
  };

  const saveDoctorVendorCommission = async () => {
    if (!doctorCommissionForm.doctor_id || !doctorCommissionForm.vendor_id) {
      toast.error('Please select both doctor and vendor');
      return;
    }
    setSavingDoctorCommission(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/doctor-vendor`, {
        doctor_id: doctorCommissionForm.doctor_id,
        vendor_id: doctorCommissionForm.vendor_id,
        commission_percent: parseFloat(doctorCommissionForm.commission_percent)
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Referral rate saved');
      setShowDoctorCommissionDialog(false);
      setDoctorCommissionForm({ doctor_id: '', vendor_id: '', commission_percent: 10 });
      fetchDoctorVendorCommissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save referral rate');
    } finally {
      setSavingDoctorCommission(false);
    }
  };

  const deleteDoctorVendorCommission = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this referral rate?')) return;
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/doctor-vendor/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referral rate deleted');
      fetchDoctorVendorCommissions();
    } catch (err) {
      toast.error('Failed to delete referral rate');
    }
  };

  const saveGlobalDefaultCommission = async (newPercent) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/global-default`, {
        default_commission_percent: parseFloat(newPercent)
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Global default referral rate updated');
      setGlobalDefaultCommission(newPercent);
    } catch (err) {
      toast.error('Failed to update global default');
    }
  };

  // ============= Admin Patient Orders Functions =============
  const fetchAdminPatientOrders = async (page = 1) => {
    setAdminPatientOrdersLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (adminPatientOrdersFilters.search) params.append('search', adminPatientOrdersFilters.search);
      if (adminPatientOrdersFilters.vendor_search) params.append('vendor_search', adminPatientOrdersFilters.vendor_search);
      if (adminPatientOrdersFilters.vendor_type && adminPatientOrdersFilters.vendor_type !== 'all') params.append('vendor_type', adminPatientOrdersFilters.vendor_type);
      if (adminPatientOrdersFilters.status && adminPatientOrdersFilters.status !== 'all') params.append('status', adminPatientOrdersFilters.status);
      if (adminPatientOrdersFilters.date_from) params.append('date_from', adminPatientOrdersFilters.date_from);
      if (adminPatientOrdersFilters.date_to) params.append('date_to', adminPatientOrdersFilters.date_to);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/patient-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAdminPatientOrders(res.data.orders || []);
      setAdminPatientOrdersSummary(res.data.summary);
      setAdminPatientOrdersTotal(res.data.total || 0);
      setAdminPatientOrdersPage(page);
    } catch (err) {
      console.error('Failed to fetch patient orders', err);
      toast.error('Failed to fetch patient orders');
    } finally {
      setAdminPatientOrdersLoading(false);
    }
  };

  // ============= Consultation Coupons Functions =============
  const fetchConsultCoupons = async () => {
    setConsultCouponsLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/consultation-coupons/admin/list`, { headers: { Authorization: `Bearer ${token}` }});
      setConsultCoupons(res.data || []);
    } catch { toast.error('Failed to load coupons'); }
    setConsultCouponsLoading(false);
  };

  const createConsultCoupon = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/consultation-coupons/admin/create`, {
        ...couponForm,
        discount_value: parseFloat(couponForm.discount_value) || 0,
        max_uses_total: parseInt(couponForm.max_uses_total) || 0,
        max_uses_per_patient: parseInt(couponForm.max_uses_per_patient) || 0,
        valid_from: couponForm.valid_from || null,
        valid_to: couponForm.valid_to || null,
        applicable_doctors: couponDoctorScope === 'all' ? [] : couponForm.applicable_doctors,
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Coupon created');
      setShowCouponForm(false);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', valid_from: '', valid_to: '', description: '', applicable_doctors: [] });
      setCouponDoctorScope('all');
      fetchConsultCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create coupon'); }
  };

  const fetchCouponDoctors = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/consultation-coupons/admin/doctors-list`, { headers: { Authorization: `Bearer ${token}` }});
      setCouponDoctors(res.data || []);
    } catch { /* ignore */ }
  };

  const deleteConsultCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/consultation-coupons/admin/${id}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Coupon deleted');
      fetchConsultCoupons();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleConsultCoupon = async (id, active) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/consultation-coupons/admin/${id}`, { is_active: !active }, { headers: { Authorization: `Bearer ${token}` }});
      fetchConsultCoupons();
    } catch { toast.error('Failed to update'); }
  };

  const fetchAdminConsultations = async (page = 1) => {
    setAdminConsultLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams({ page, limit: 50 });
      if (adminConsultFilters.search) params.append('search', adminConsultFilters.search);
      if (adminConsultFilters.status !== 'all') params.append('status', adminConsultFilters.status);
      if (adminConsultFilters.has_coupon !== 'all') params.append('has_coupon', adminConsultFilters.has_coupon);
      if (adminConsultFilters.date_from) params.append('date_from', adminConsultFilters.date_from);
      if (adminConsultFilters.date_to) params.append('date_to', adminConsultFilters.date_to);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/consultation-coupons/admin/consultations?${params}`, { headers: { Authorization: `Bearer ${token}` }});
      setAdminConsultations(res.data.consultations || []);
      setAdminConsultTotal(res.data.total || 0);
      setAdminConsultSummary(res.data.summary || null);
      setAdminConsultPage(page);
    } catch { toast.error('Failed to load consultations'); }
    setAdminConsultLoading(false);
  };

  // ============= Admin B2B Orders Functions =============
  const fetchAdminB2BOrders = async (page = 1) => {
    setAdminB2BOrdersLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (adminB2BOrdersFilters.search) params.append('search', adminB2BOrdersFilters.search);
      if (adminB2BOrdersFilters.vendor_search) params.append('vendor_search', adminB2BOrdersFilters.vendor_search);
      if (adminB2BOrdersFilters.vendor_type && adminB2BOrdersFilters.vendor_type !== 'all') params.append('vendor_type', adminB2BOrdersFilters.vendor_type);
      if (adminB2BOrdersFilters.status && adminB2BOrdersFilters.status !== 'all') params.append('status', adminB2BOrdersFilters.status);
      if (adminB2BOrdersFilters.date_from) params.append('date_from', adminB2BOrdersFilters.date_from);
      if (adminB2BOrdersFilters.date_to) params.append('date_to', adminB2BOrdersFilters.date_to);
      if (adminB2BOrdersFilters.payment_method && adminB2BOrdersFilters.payment_method !== 'all') params.append('payment_method', adminB2BOrdersFilters.payment_method);
      if (adminB2BOrdersFilters.order_type && adminB2BOrdersFilters.order_type !== 'all') params.append('order_type', adminB2BOrdersFilters.order_type);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/b2b-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAdminB2BOrders(res.data.orders || []);
      setAdminB2BOrdersSummary(res.data.summary);
      setAdminB2BOrdersTotal(res.data.total || 0);
      setAdminB2BOrdersPage(page);
    } catch (err) {
      console.error('Failed to fetch B2B orders', err);
      toast.error('Failed to fetch B2B orders');
    } finally {
      setAdminB2BOrdersLoading(false);
    }
  };

  const fetchAdminChemistOrders = async () => {
    setAdminChemistOrdersLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission/b2b-orders?buyer_type=chemist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminChemistOrders(res.data.orders || []);
    } catch (err) { console.error(err); }
    setAdminChemistOrdersLoading(false);
  };


  const getVendorIcon = (type) => {
    switch (type) {
      case 'lab': return <FlaskConical className="w-4 h-4" />;
      case 'pharma': return <Pill className="w-4 h-4" />;
      case 'inventory': return <Package className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      suspended: 'bg-gray-100 text-gray-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filteredVendorsList = allVendors.filter(v => {
    const matchesSearch = !vendorSearch ||
      (v.name || '').toLowerCase().includes(vendorSearch.toLowerCase()) ||
      (v.email || '').toLowerCase().includes(vendorSearch.toLowerCase()) ||
      (v.phone || '').includes(vendorSearch);
    const matchesType = vendorTypeFilter === 'all' || v.vendor_type === vendorTypeFilter;
    const matchesStatus = vendorStatusFilter === 'all' || v.status === vendorStatusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const filteredClinicPatients = allPatients.filter(p => {
    if (!patientSearch) return true;
    const search = patientSearch.toLowerCase();
    return (p.name || '').toLowerCase().includes(search) ||
           (p.phone || '').includes(search) ||
           (p.email || '').toLowerCase().includes(search);
  });

  return (
    <div className="min-h-screen bg-slate-100 overflow-x-hidden">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="w-9 h-9 md:w-10 md:h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base md:text-xl font-bold truncate">Super Admin</h1>
              <p className="text-purple-200 text-xs md:text-sm hidden sm:block">1Clin Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <DownloadAppButton 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/20 hidden md:flex"
            />
            <NotificationBell portalType="super_admin" />
            <span className="text-purple-200 text-sm hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogoutClick} className="text-white hover:bg-white/20 p-2 md:px-3">
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6 pb-6">
        {/* Stats Cards - 2x2 grid on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <Card data-testid="total-vendors-card">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-slate-500 truncate">Vendors</p>
                  <p className="text-lg md:text-2xl font-bold text-slate-800">{stats?.vendors?.total || 0}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-1 flex gap-2 text-[10px] md:text-xs">
                <span className="text-green-600">{stats?.vendors?.approved || 0} ok</span>
                <span className="text-yellow-600">{stats?.vendors?.pending || 0} pen</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-orders-card">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-slate-500 truncate">Orders</p>
                  <p className="text-lg md:text-2xl font-bold text-slate-800">{stats?.orders?.total || 0}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-1 flex gap-2 text-[10px] md:text-xs">
                <span className="text-yellow-600">{stats?.orders?.pending || 0} pen</span>
                <span className="text-green-600">{stats?.orders?.completed || 0} done</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-revenue-card">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-slate-500 truncate">Revenue</p>
                  <p className="text-lg md:text-2xl font-bold text-slate-800">₹{(stats?.revenue?.total || 0).toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <IndianRupee className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-1 text-[10px] md:text-xs text-green-600 truncate">
                ₹{(stats?.revenue?.this_month || 0).toLocaleString()} /mo
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-clinics-card">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-slate-500 truncate">Clinics</p>
                  <p className="text-lg md:text-2xl font-bold text-slate-800">{stats?.clinics?.total || 0}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 md:w-6 md:h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vendor Type Breakdown - Hidden on mobile, shown on tablet+ */}
        <div className="hidden md:grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <FlaskConical className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Labs</p>
                <p className="text-xl font-bold">{stats?.vendors?.by_type?.lab || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <Pill className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pharma</p>
                <p className="text-xl font-bold">{stats?.vendors?.by_type?.pharma || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Inventory</p>
                <p className="text-xl font-bold">{stats?.vendors?.by_type?.inventory || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/lis')} data-testid="admin-lis-link">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FlaskConical className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Lab Management</p>
                <p className="text-xs text-slate-500">Admin Panel</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-teal-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/labs-store')} data-testid="admin-labs-store-link">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <Store className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-teal-600 font-medium">Labs & Store</p>
                <p className="text-xs text-slate-500">Curate Featured Items</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile-friendly Tab Navigation */}
        <MobileTabNav
          tabs={[
            { id: 'overview', label: 'Overview', icon: LayoutGrid },
            { id: 'pending', label: 'Pending', icon: Clock, badge: pendingVendors.length > 0 ? pendingVendors.length : null },
            { id: 'vendors', label: 'Vendors', icon: Building2 },
            { id: 'mappings', label: 'Links', icon: Link2, onClick: fetchVendorDoctorMappings },
            { id: 'patient-orders', label: 'Patient Orders', icon: Users, onClick: fetchAdminPatientOrders },
            { id: 'b2b-orders', label: 'B2B Orders', icon: Briefcase, onClick: fetchAdminB2BOrders },
            { id: 'chemist-orders', label: 'Chemist Orders', icon: Pill, onClick: fetchAdminChemistOrders },
            { id: 'payments', label: 'Payments', icon: CreditCard, badge: (pendingTopups.length + vendorPayouts.length) > 0 ? pendingTopups.length + vendorPayouts.length : null, onClick: () => { fetchPaymentSettings(); fetchVendorPayouts(); fetchVendorTopupRequests(); fetchRedemptionRequests(); fetchVendorWallets(); } },
            { id: 'doctor-commissions', label: 'Referral Ledger', icon: Percent, onClick: () => { fetchDoctorVendorCommissions(); fetchAdminReferralLedger(); } },
            { id: 'vendor-billing', label: 'Vendor Billing', icon: Receipt, onClick: () => fetchVendorBills(1) },
            { id: 'subscriptions', label: 'Subscriptions', icon: Star, onClick: () => { fetchSubscriptionPlans(); fetchDoctorsList(); fetchOffers(); fetchOfferClaims(); } },
            { id: 'consult-coupons', label: 'Consult Coupons', icon: Tag, onClick: fetchConsultCoupons },
            { id: 'online-consultations', label: 'Online Consults', icon: Video, onClick: () => fetchAdminConsultations(1) },
            { id: 'patients', label: 'Patients', icon: Users },
            { id: 'push-notifications', label: 'Push Alerts', icon: Send },
            { id: 'support-tickets', label: 'Support Tickets', icon: MessageSquare, onClick: fetchAdminTickets },
            { id: 'profile', label: 'Profile', icon: UserCircle, onClick: fetchAdminProfile },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tabs Section - Hidden TabsList, content controlled by MobileTabNav */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="hidden">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="vendors">Vendors</TabsTrigger>
            <TabsTrigger value="mappings">Mappings</TabsTrigger>
            <TabsTrigger value="patient-orders">Patient Orders</TabsTrigger>
            <TabsTrigger value="b2b-orders">B2B Orders</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="doctor-commissions">Referral Ledger</TabsTrigger>
            <TabsTrigger value="vendor-billing">Vendor Billing</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="consult-coupons">Consult Coupons</TabsTrigger>
            <TabsTrigger value="online-consultations">Online Consults</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="support-tickets">Support Tickets</TabsTrigger>
            <TabsTrigger value="push-notifications">Push Alerts</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recent Orders */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.recent_orders?.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{order.order_number}</p>
                          <p className="text-xs text-slate-500">{order.clinic_name} → {order.vendor_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm">₹{order.total?.toLocaleString()}</p>
                          {getStatusBadge(order.status)}
                        </div>
                      </div>
                    ))}
                    {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                      <p className="text-slate-500 text-center py-4">No orders yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Vendors */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Vendors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.recent_vendors?.slice(0, 5).map((vendor) => (
                      <div key={vendor.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            {getVendorIcon(vendor.vendor_type)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{vendor.name}</p>
                            <p className="text-xs text-slate-500 capitalize">{vendor.vendor_type}</p>
                          </div>
                        </div>
                        {getStatusBadge(vendor.status)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Banner Ads Management */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="w-5 h-5 text-indigo-600" /> Banner Ads (All Doctors)
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Banner title (optional)"
                      value={bannerTitle}
                      onChange={(e) => setBannerTitle(e.target.value)}
                      className="w-48 h-9 text-sm"
                    />
                    <input
                      type="file"
                      ref={bannerFileRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadAdminBanner(f);
                        e.target.value = '';
                      }}
                    />
                    <Button size="sm" onClick={() => { fetchAdminBanners(); bannerFileRef.current?.click(); }} disabled={uploadingBanner} data-testid="upload-admin-banner-btn">
                      {uploadingBanner ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Add Banner
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Recommended size: <span className="font-semibold text-slate-700">1200 x 300 px</span> (4:1 ratio, landscape). Max 5 MB. JPG/PNG/WebP.</p>
              </CardHeader>
              <CardContent>
                {adminBanners.length === 0 ? (
                  <p className="text-center text-slate-500 py-6">No banners yet. Upload banner images to show on all doctor dashboards.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {adminBanners.map(banner => (
                      <div key={banner.id} className={`relative rounded-lg overflow-hidden border ${banner.is_active ? 'border-indigo-200' : 'border-slate-200 opacity-50'}`} data-testid={`admin-banner-${banner.id}`}>
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${banner.image_url}`}
                          alt={banner.title || 'Banner'}
                          className="w-full h-32 object-cover"
                        />
                        <div className="p-2 bg-white flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700 truncate flex-1">{banner.title || 'Untitled'}</p>
                          <div className="flex items-center gap-1">
                            <Switch checked={banner.is_active} onCheckedChange={() => toggleBannerActive(banner)} />
                            <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0" onClick={() => deleteAdminBanner(banner.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending">
            {/* Vendor Auto-Approve Toggle */}
            <Card className="mb-4">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Vendor Auto-Approve</p>
                  <p className="text-xs text-slate-500">When enabled, new vendors can login immediately without admin approval</p>
                </div>
                <div className="flex items-center gap-2">
                  {savingPlatformConfig && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Switch 
                    checked={platformConfig.vendor_auto_approve} 
                    onCheckedChange={toggleVendorAutoApprove}
                    data-testid="vendor-auto-approve-toggle"
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  Pending Vendor Approvals
                </CardTitle>
                <CardDescription>
                  {platformConfig.vendor_auto_approve 
                    ? 'Auto-approve is ON. New vendors will be automatically approved.'
                    : 'Review and approve vendor applications'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingVendors.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p>No pending approvals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingVendors.map((vendor) => (
                      <div key={vendor.id} className="border rounded-lg p-3 sm:p-4" data-testid={`pending-vendor-${vendor.id}`}>
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            {getVendorIcon(vendor.vendor_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm sm:text-base">{vendor.name}</h3>
                            <p className="text-xs sm:text-sm text-slate-500 capitalize">{vendor.vendor_type} Vendor</p>
                            <div className="mt-2 text-xs sm:text-sm space-y-1">
                              <p className="truncate"><span className="text-slate-500">Email:</span> {vendor.email}</p>
                              <p><span className="text-slate-500">Phone:</span> {vendor.phone}</p>
                              <p><span className="text-slate-500">GST:</span> {vendor.gst_number || 'N/A'}</p>
                              {vendor.license_number && <p><span className="text-slate-500">License:</span> {vendor.license_number}</p>}
                            </div>
                          </div>
                        </div>
                        {/* Action buttons - always visible on separate row for mobile */}
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          <Button
                            size="sm"
                            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 h-9"
                            onClick={() => handleVendorAction(vendor.id, 'approved')}
                            data-testid={`approve-btn-${vendor.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1 sm:flex-none h-9"
                            onClick={() => handleVendorAction(vendor.id, 'rejected', 'Does not meet requirements')}
                            data-testid={`reject-btn-${vendor.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vendors">
            <Card>
              <CardHeader>
                <CardTitle>All Vendors</CardTitle>
                <CardDescription>Manage all registered vendors on the platform</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search vendors by name, email, or phone..."
                      value={vendorSearch}
                      onChange={(e) => setVendorSearch(e.target.value)}
                      className="pl-10"
                      data-testid="vendor-search"
                    />
                  </div>
                  <Select value={vendorTypeFilter} onValueChange={setVendorTypeFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="lab">Lab</SelectItem>
                      <SelectItem value="pharma">Pharma</SelectItem>
                      <SelectItem value="inventory">Inventory</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={vendorStatusFilter} onValueChange={setVendorStatusFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedVendorIds.length > 0 && (
                  <div className="mb-3 flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg" data-testid="vendor-bulk-bar">
                    <span className="text-sm font-medium text-red-700">{selectedVendorIds.length} selected</span>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteConfirm({ type: 'vendor', ids: selectedVendorIds, names: allVendors.filter(v => selectedVendorIds.includes(v.id)).map(v => v.name) })} data-testid="bulk-delete-vendors-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 w-8">
                          <input type="checkbox" checked={filteredVendorsList.length > 0 && selectedVendorIds.length === filteredVendorsList.length} onChange={() => setSelectedVendorIds(prev => prev.length === filteredVendorsList.length ? [] : filteredVendorsList.map(v => v.id))} data-testid="select-all-vendors-checkbox" />
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Vendor</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Contact</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVendorsList
                        .map((vendor) => (
                        <tr key={vendor.id} className="border-b hover:bg-slate-50 cursor-pointer" data-testid={`vendor-row-${vendor.id}`}>
                          <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selectedVendorIds.includes(vendor.id)} onChange={() => setSelectedVendorIds(prev => prev.includes(vendor.id) ? prev.filter(x => x !== vendor.id) : [...prev, vendor.id])} data-testid={`select-vendor-${vendor.id}`} />
                          </td>
                          <td className="py-3 px-4" onClick={() => fetchVendorDetails(vendor.id)}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                                {getVendorIcon(vendor.vendor_type)}
                              </div>
                              <span className="font-medium text-blue-600 hover:underline">{vendor.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 capitalize" onClick={() => fetchVendorDetails(vendor.id)}>{vendor.vendor_type}</td>
                          <td className="py-3 px-4" onClick={() => fetchVendorDetails(vendor.id)}>
                            <p className="text-sm">{vendor.email}</p>
                            <p className="text-xs text-slate-500">{vendor.phone}</p>
                          </td>
                          <td className="py-3 px-4" onClick={() => fetchVendorDetails(vendor.id)}>{getStatusBadge(vendor.status)}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={(e) => { e.stopPropagation(); fetchVendorDetails(vendor.id); }}
                              >
                                <Settings className="w-3 h-3 mr-1" /> Referral Config
                              </Button>
                              {vendor.status === 'approved' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); handleVendorAction(vendor.id, 'suspended'); }}
                                >
                                  Suspend
                                </Button>
                              )}
                              {vendor.status === 'suspended' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); handleVendorAction(vendor.id, 'approved'); }}
                                >
                                  Reactivate
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: 'vendor', ids: [vendor.id], names: [vendor.name] }); }}
                                data-testid={`delete-vendor-${vendor.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredVendorsList.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No vendors found matching your filters</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendor-Doctor Mappings Tab */}
          <TabsContent value="mappings">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-purple-600" /> Vendor-Doctor Connections
                    </CardTitle>
                    <CardDescription>Manage which vendors can sell to which doctors</CardDescription>
                  </div>
                  <Button onClick={() => setCreateMappingOpen(true)} data-testid="create-mapping-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create Connection
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <Select value={mappingFilter.status} onValueChange={(v) => setMappingFilter({...mappingFilter, status: v})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={mappingFilter.vendor_type} onValueChange={(v) => setMappingFilter({...mappingFilter, vendor_type: v})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Vendor Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="lab">Lab</SelectItem>
                      <SelectItem value="pharma">Pharma</SelectItem>
                      <SelectItem value="inventory">Inventory</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Mappings Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left p-3 font-medium">Vendor</th>
                        <th className="text-left p-3 font-medium">Type</th>
                        <th className="text-left p-3 font-medium">Doctor</th>
                        <th className="text-left p-3 font-medium">Clinic</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Requested By</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorDoctorMappings
                        .filter(m => mappingFilter.status === 'all' || m.status === mappingFilter.status)
                        .filter(m => mappingFilter.vendor_type === 'all' || m.vendor_type === mappingFilter.vendor_type)
                        .map(mapping => (
                        <tr key={mapping.id} className="border-b hover:bg-slate-50" data-testid={`mapping-${mapping.id}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {getVendorIcon(mapping.vendor_type)}
                              <span className="font-medium">{mapping.vendor_name}</span>
                            </div>
                          </td>
                          <td className="p-3 capitalize">{mapping.vendor_type}</td>
                          <td className="p-3">{mapping.doctor_name}</td>
                          <td className="p-3 text-sm text-slate-500">{mapping.clinic_name || '-'}</td>
                          <td className="p-3">
                            <Badge className={getStatusBadge(mapping.status)}>{mapping.status}</Badge>
                          </td>
                          <td className="p-3 text-sm capitalize">{mapping.requester_type || 'admin'}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {mapping.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="outline" className="text-green-600" onClick={() => approveMapping(mapping.id)}>
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectMapping(mapping.id)}>
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteMapping(mapping.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {vendorDoctorMappings.length === 0 && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center text-slate-500">
                            No vendor-doctor connections yet. Create one to get started.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Create Mapping Dialog */}
            <Dialog open={createMappingOpen} onOpenChange={setCreateMappingOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Vendor-Doctor Connection</DialogTitle>
                  <DialogDescription>Link a vendor to a doctor to allow them to trade.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Select Vendor</Label>
                    <Select value={newMapping.vendor_id} onValueChange={(v) => setNewMapping({...newMapping, vendor_id: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Choose vendor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {allVendors.filter(v => v.status === 'approved').map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name} ({v.vendor_type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Select Doctor</Label>
                    <Select value={newMapping.doctor_id} onValueChange={(v) => setNewMapping({...newMapping, doctor_id: v})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Choose doctor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {doctors.map(d => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.doctor_name || d.name} - {d.clinic_name || 'Clinic'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={newMapping.notes}
                      onChange={(e) => setNewMapping({...newMapping, notes: e.target.value})}
                      placeholder="Any notes about this connection..."
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setCreateMappingOpen(false)}>Cancel</Button>
                    <Button onClick={createVendorDoctorMapping} disabled={creatingMapping}>
                      {creatingMapping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Create Connection
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
                <CardDescription>View all B2B orders on the platform</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by order #, clinic, or vendor..."
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      className="pl-10"
                      data-testid="order-search"
                    />
                  </div>
                  <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Order #</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Clinic</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Vendor</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allOrders
                        .filter(o => {
                          const matchesSearch = !orderSearch || 
                            (o.order_number || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                            (o.clinic_name || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                            (o.vendor_name || '').toLowerCase().includes(orderSearch.toLowerCase());
                          const matchesStatus = orderStatusFilter === 'all' || o.status === orderStatusFilter;
                          return matchesSearch && matchesStatus;
                        })
                        .map((order) => (
                        <tr key={order.id} className="border-b hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                          <td className="py-3 px-4 font-mono text-sm">{order.order_number}</td>
                          <td className="py-3 px-4">{order.clinic_name}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {getVendorIcon(order.vendor_type)}
                              <span>{order.vendor_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-medium">₹{order.total?.toLocaleString()}</td>
                          <td className="py-3 px-4">{getStatusBadge(order.status)}</td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {allOrders.filter(o => {
                    const matchesSearch = !orderSearch || 
                      (o.order_number || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                      (o.clinic_name || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                      (o.vendor_name || '').toLowerCase().includes(orderSearch.toLowerCase());
                    const matchesStatus = orderStatusFilter === 'all' || o.status === orderStatusFilter;
                    return matchesSearch && matchesStatus;
                  }).length === 0 && (
                    <p className="text-center py-8 text-slate-500">No orders found matching your filters</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patient Orders Tab - Orders from Patient Portal with Commission Breakdown */}
          <TabsContent value="patient-orders">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600 font-medium">Admin Commission</p>
                    <p className="text-2xl font-bold text-green-700">₹{adminPatientOrdersSummary?.total_admin_commission?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total Orders</p>
                    <p className="text-2xl font-bold text-slate-800">{adminPatientOrdersSummary?.order_count || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Patient MRP</p>
                    <p className="text-2xl font-bold text-blue-600">₹{adminPatientOrdersSummary?.total_patient_mrp?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">GST</p>
                    <p className="text-2xl font-bold text-slate-600">₹{adminPatientOrdersSummary?.total_gst?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Vendor Payout</p>
                    <p className="text-2xl font-bold text-purple-600">₹{adminPatientOrdersSummary?.total_vendor_payout?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-500" /> Patient Portal Orders
                  </CardTitle>
                  <CardDescription>All orders from the Patient Portal marketplace with referral charges breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by order #, patient, doctor..."
                        value={adminPatientOrdersFilters.search}
                        onChange={(e) => setAdminPatientOrdersFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Vendor name, phone, email..."
                        value={adminPatientOrdersFilters.vendor_search}
                        onChange={(e) => setAdminPatientOrdersFilters(prev => ({ ...prev, vendor_search: e.target.value }))}
                        className="pl-10"
                        data-testid="patient-orders-vendor-search"
                      />
                    </div>
                    <Select value={adminPatientOrdersFilters.vendor_type} onValueChange={(v) => setAdminPatientOrdersFilters(prev => ({ ...prev, vendor_type: v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Vendor Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="lab">Lab</SelectItem>
                        <SelectItem value="pharma">Pharma</SelectItem>
                        <SelectItem value="inventory">Inventory</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adminPatientOrdersFilters.status} onValueChange={(v) => setAdminPatientOrdersFilters(prev => ({ ...prev, status: v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="date" value={adminPatientOrdersFilters.date_from} onChange={(e) => setAdminPatientOrdersFilters(prev => ({ ...prev, date_from: e.target.value }))} className="w-40" />
                    <Input type="date" value={adminPatientOrdersFilters.date_to} onChange={(e) => setAdminPatientOrdersFilters(prev => ({ ...prev, date_to: e.target.value }))} className="w-40" />
                    <Button onClick={() => fetchAdminPatientOrders(1)}>
                      <Search className="w-4 h-4 mr-1" /> Search
                    </Button>
                    <Button variant="outline" onClick={() => { setAdminPatientOrdersFilters({ search: '', vendor_search: '', vendor_type: 'all', status: 'all', date_from: '', date_to: '' }); fetchAdminPatientOrders(1); }}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Reset
                    </Button>
                  </div>

                  {adminPatientOrdersLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Order Details</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Vendor(s)</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Products</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Patient MRP</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Admin Comm.</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">GST</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Vendor Payout</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminPatientOrders.map((order) => {
                            // Extract unique vendors from items
                            const vendors = [...new Map(order.items?.map(item => [item.vendor_id, { name: item.vendor_name, type: item.vendor_type }]) || []).values()];
                            return (
                            <tr key={order.id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4 text-sm">{new Date(order.created_at).toLocaleDateString('en-IN')}</td>
                              <td className="py-3 px-4">
                                <p className="font-medium text-slate-800">{order.order_number}</p>
                                <p className="text-xs text-slate-500">Patient: {order.patient_name}</p>
                                <p className="text-xs text-slate-400">Dr. {order.prescribing_doctor_name}</p>
                              </td>
                              <td className="py-3 px-4">
                                {vendors.map((v, idx) => (
                                  <div key={idx} className="mb-1">
                                    <p className="font-medium text-teal-700 text-sm">{v.name || 'Unknown'}</p>
                                    {v.type && <p className="text-xs text-slate-400">{v.type}</p>}
                                  </div>
                                ))}
                              </td>
                              <td className="py-3 px-4">
                                {order.items?.slice(0, 2).map((item, idx) => (
                                  <p key={idx} className="text-xs text-slate-600">{item.product_name} (x{item.quantity})</p>
                                ))}
                                {order.items?.length > 2 && <p className="text-xs text-slate-400">+{order.items.length - 2} more</p>}
                              </td>
                              <td className="py-3 px-4 text-right font-medium">₹{order.subtotal?.toLocaleString() || 0}</td>
                              <td className="py-3 px-4 text-right text-green-600 font-medium">₹{order.admin_commission?.toLocaleString() || 0}</td>
                              <td className="py-3 px-4 text-right text-slate-500 font-medium">{(order.gst_amount || 0) > 0 ? `₹${order.gst_amount.toLocaleString()}` : '-'}</td>
                              <td className="py-3 px-4 text-right text-purple-600 font-medium">₹{order.vendor_payout?.toLocaleString() || 0}</td>
                              <td className="py-3 px-4 text-center">{getStatusBadge(order.status)}</td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                      {adminPatientOrders.length === 0 && (
                        <p className="text-center py-8 text-slate-500">No patient orders found</p>
                      )}
                      {adminPatientOrdersTotal > 20 && (
                        <div className="flex justify-center gap-2 mt-4">
                          <Button variant="outline" size="sm" disabled={adminPatientOrdersPage <= 1} onClick={() => fetchAdminPatientOrders(adminPatientOrdersPage - 1)}>Previous</Button>
                          <span className="px-3 py-1 text-sm text-slate-600">Page {adminPatientOrdersPage} of {Math.ceil(adminPatientOrdersTotal / 20)}</span>
                          <Button variant="outline" size="sm" disabled={adminPatientOrdersPage >= Math.ceil(adminPatientOrdersTotal / 20)} onClick={() => fetchAdminPatientOrders(adminPatientOrdersPage + 1)}>Next</Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* B2B Orders Tab - Orders from Doctors with Commission Breakdown */}
          <TabsContent value="b2b-orders">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600 font-medium">Admin Commission</p>
                    <p className="text-2xl font-bold text-green-700">₹{adminB2BOrdersSummary?.total_admin_commission?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total Orders</p>
                    <p className="text-2xl font-bold text-slate-800">{adminB2BOrdersSummary?.order_count || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total MRP</p>
                    <p className="text-2xl font-bold text-blue-600">₹{adminB2BOrdersSummary?.total_mrp?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total B2B Price</p>
                    <p className="text-2xl font-bold text-teal-600">₹{adminB2BOrdersSummary?.total_b2b_price?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">GST</p>
                    <p className="text-2xl font-bold text-slate-600">₹{adminB2BOrdersSummary?.total_gst?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Vendor Payout</p>
                    <p className="text-2xl font-bold text-purple-600">₹{adminB2BOrdersSummary?.total_vendor_payout?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-teal-500" /> B2B Orders (from Doctors)
                  </CardTitle>
                  <CardDescription>All orders placed by doctors from the B2B marketplace with commission breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by order #, doctor, clinic..."
                        value={adminB2BOrdersFilters.search}
                        onChange={(e) => setAdminB2BOrdersFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Vendor name, phone, email..."
                        value={adminB2BOrdersFilters.vendor_search}
                        onChange={(e) => setAdminB2BOrdersFilters(prev => ({ ...prev, vendor_search: e.target.value }))}
                        className="pl-10"
                        data-testid="b2b-orders-vendor-search"
                      />
                    </div>
                    <Select value={adminB2BOrdersFilters.vendor_type} onValueChange={(v) => setAdminB2BOrdersFilters(prev => ({ ...prev, vendor_type: v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Vendor Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="lab">Lab</SelectItem>
                        <SelectItem value="pharma">Pharma</SelectItem>
                        <SelectItem value="inventory">Inventory</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adminB2BOrdersFilters.status} onValueChange={(v) => setAdminB2BOrdersFilters(prev => ({ ...prev, status: v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adminB2BOrdersFilters.payment_method || 'all'} onValueChange={(v) => setAdminB2BOrdersFilters(prev => ({ ...prev, payment_method: v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Payment" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Payment</SelectItem>
                        <SelectItem value="wallet">Wallet</SelectItem>
                        <SelectItem value="credit">On Credit</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adminB2BOrdersFilters.order_type || 'all'} onValueChange={(v) => setAdminB2BOrdersFilters(prev => ({ ...prev, order_type: v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="purchase">Purchase</SelectItem>
                        <SelectItem value="subscription">Subscription</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="date" value={adminB2BOrdersFilters.date_from} onChange={(e) => setAdminB2BOrdersFilters(prev => ({ ...prev, date_from: e.target.value }))} className="w-40" />
                    <Input type="date" value={adminB2BOrdersFilters.date_to} onChange={(e) => setAdminB2BOrdersFilters(prev => ({ ...prev, date_to: e.target.value }))} className="w-40" />
                    <Button onClick={() => fetchAdminB2BOrders(1)}>
                      <Search className="w-4 h-4 mr-1" /> Search
                    </Button>
                    <Button variant="outline" onClick={() => { setAdminB2BOrdersFilters({ search: '', vendor_search: '', vendor_type: 'all', status: 'all', payment_method: 'all', order_type: 'all', date_from: '', date_to: '' }); fetchAdminB2BOrders(1); }}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Reset
                    </Button>
                  </div>

                  {adminB2BOrdersLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Order Details</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Vendor</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Products</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">MRP</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">B2B Price</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Admin Comm.</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">GST</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Vendor Payout</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminB2BOrders.map((order) => (
                            <tr key={order.id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4 text-sm">{new Date(order.created_at).toLocaleDateString('en-IN')}</td>
                              <td className="py-3 px-4">
                                <p className="font-medium text-slate-800">{order.order_number}</p>
                                <p className="text-xs text-slate-500">Dr. {order.doctor_name}</p>
                                <p className="text-xs text-slate-400">{order.clinic_name}</p>
                              </td>
                              <td className="py-3 px-4">
                                <p className="font-medium text-teal-700 text-sm">{order.vendor_name || '-'}</p>
                                <p className="text-xs text-slate-400">{order.vendor_type || ''}</p>
                              </td>
                              <td className="py-3 px-4">
                                {order.items?.slice(0, 2).map((item, idx) => (
                                  <p key={idx} className="text-xs text-slate-600">{item.item_name || item.name || item.product_name} (x{item.quantity})</p>
                                ))}
                                {order.items?.length > 2 && <p className="text-xs text-slate-400">+{order.items.length - 2} more</p>}
                              </td>
                              <td className="py-3 px-4 text-right font-medium">₹{(order.mrp || order.total_mrp || 0).toLocaleString()}</td>
                              <td className="py-3 px-4 text-right text-teal-600 font-medium">₹{(order.b2b_price || order.subtotal || 0).toLocaleString()}</td>
                              <td className="py-3 px-4 text-right text-green-600 font-medium">₹{(order.platform_commission || 0).toLocaleString()}</td>
                              <td className="py-3 px-4 text-right text-slate-500 font-medium">{(order.tax || order.gst_amount || 0) > 0 ? `₹${(order.tax || order.gst_amount || 0).toLocaleString()}` : '-'}</td>
                              <td className="py-3 px-4 text-right text-purple-600 font-medium">₹{(order.vendor_payout || 0).toLocaleString()}</td>
                              <td className="py-3 px-4 text-center">
                                {getStatusBadge(order.status)}
                                {order.payment_method === 'credit' && (
                                  <Badge className={`ml-1 text-[10px] ${
                                    order.credit_status === 'paid' ? 'bg-green-100 text-green-700' :
                                    order.credit_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>{order.credit_status === 'paid' ? 'Credit Paid' : order.credit_status === 'overdue' ? 'Overdue' : 'Credit'}</Badge>
                                )}
                                {order.order_type === 'subscription' && (
                                  <Badge className="ml-1 text-[10px] bg-purple-100 text-purple-700">{order.subscription_period || 'Sub'}</Badge>
                                )}
                              </td>
                              <td className="py-1 px-2"><InvoiceUploader orderId={order.id} orderType={order.order_type || 'b2b'} canUpload={false} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {adminB2BOrders.length === 0 && (
                        <p className="text-center py-8 text-slate-500">No B2B orders found</p>
                      )}
                      {adminB2BOrdersTotal > 20 && (
                        <div className="flex justify-center gap-2 mt-4">
                          <Button variant="outline" size="sm" disabled={adminB2BOrdersPage <= 1} onClick={() => fetchAdminB2BOrders(adminB2BOrdersPage - 1)}>Previous</Button>
                          <span className="px-3 py-1 text-sm text-slate-600">Page {adminB2BOrdersPage} of {Math.ceil(adminB2BOrdersTotal / 20)}</span>
                          <Button variant="outline" size="sm" disabled={adminB2BOrdersPage >= Math.ceil(adminB2BOrdersTotal / 20)} onClick={() => fetchAdminB2BOrders(adminB2BOrdersPage + 1)}>Next</Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Doctor Commissions Tab */}
          <TabsContent value="doctor-commissions">
            <div className="space-y-6">
              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Referral Ledger — Tracking only. Doctor commissions are <b>not auto-debited/credited</b> to any wallet. This is an informational record for compliance.</span>
              </div>

              {/* Commission Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Default Referral Rate</p>
                        <p className="text-2xl font-bold text-teal-600">{globalDefaultCommission}%</p>
                      </div>
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                        <Percent className="w-6 h-6 text-teal-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Referral Charges Recorded</p>
                        <p className="text-2xl font-bold text-green-600">₹{(commissionEarningsReport?.summary?.total_doctor_commission || 0).toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <IndianRupee className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Patient Orders Value</p>
                        <p className="text-2xl font-bold text-blue-600">₹{(commissionEarningsReport?.summary?.total_patient_orders_value || 0).toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <ShoppingCart className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Custom Rates Set</p>
                        <p className="text-2xl font-bold text-purple-600">{doctorVendorCommissions.length}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Settings className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Global Default Commission Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-slate-600" />
                    Global Default Referral Rate
                  </CardTitle>
                  <CardDescription>
                    This is the default referral charge rate applied when no custom rate is set for a doctor-vendor pair.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label>Default Rate:</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={globalDefaultCommission}
                        onChange={(e) => setGlobalDefaultCommission(parseFloat(e.target.value) || 10)}
                        className="w-24"
                      />
                      <span className="text-slate-500">%</span>
                    </div>
                    <Button onClick={() => saveGlobalDefaultCommission(globalDefaultCommission)}>
                      <Save className="w-4 h-4 mr-1" /> Save Default
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Referral Ledger - All Transactions */}
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <IndianRupee className="w-5 h-5 text-amber-600" /> Referral Ledger
                      </CardTitle>
                      <CardDescription>Doctor referral charges on all patient & B2B orders — tracked separately from vendor payouts</CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Grand Total</p>
                      <p className="text-2xl font-bold text-amber-600">₹{(adminLedgerGrandTotal || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="relative flex-1 min-w-[180px] max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input placeholder="Search doctor, vendor, order..." value={adminLedgerFilters.search} onChange={e => setAdminLedgerFilters(f => ({ ...f, search: e.target.value }))} className="pl-10" data-testid="ledger-search" />
                    </div>
                    <Select value={adminLedgerFilters.source_type || 'all'} onValueChange={v => setAdminLedgerFilters(f => ({ ...f, source_type: v === 'all' ? '' : v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Order Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="patient_order">Patient Order</SelectItem>
                        <SelectItem value="b2b_order">B2B Order</SelectItem>
                        <SelectItem value="walkin_billing">Walk-in Billing</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adminLedgerFilters.vendor_type || 'all'} onValueChange={v => setAdminLedgerFilters(f => ({ ...f, vendor_type: v === 'all' ? '' : v }))}>
                      <SelectTrigger className="w-36"><SelectValue placeholder="Vendor Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Vendors</SelectItem>
                        <SelectItem value="chemist">Chemist</SelectItem>
                        <SelectItem value="lab">Lab</SelectItem>
                        <SelectItem value="pharma">Pharma</SelectItem>
                        <SelectItem value="equipment">Equipment</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="date" className="w-36" value={adminLedgerFilters.date_from} onChange={e => setAdminLedgerFilters(f => ({ ...f, date_from: e.target.value }))} />
                    <Input type="date" className="w-36" value={adminLedgerFilters.date_to} onChange={e => setAdminLedgerFilters(f => ({ ...f, date_to: e.target.value }))} />
                    <Button variant="outline" onClick={() => fetchAdminReferralLedger(1)} data-testid="ledger-apply-btn"><Search className="w-4 h-4 mr-1" /> Apply</Button>
                    <Button variant="ghost" onClick={() => { setAdminLedgerFilters({ search: '', source_type: '', vendor_type: '', vendor_name: '', date_from: '', date_to: '' }); fetchAdminReferralLedger(1); }}><X className="w-4 h-4 mr-1" /> Clear</Button>
                  </div>

                  {/* Doctor Summaries */}
                  {adminLedgerDoctorSummaries.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {adminLedgerDoctorSummaries.slice(0, 8).map(ds => (
                        <div key={ds.doctor_id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm font-medium text-slate-800 truncate">{ds.doctor_name}</p>
                          <p className="text-lg font-bold text-amber-700">₹{(ds.total_commission || 0).toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{ds.order_count} entries • {ds.vendors?.join(', ') || '-'}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Ledger Table */}
                  {adminLedgerLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
                  ) : adminLedgerEntries.length === 0 ? (
                    <div className="text-center py-8 text-slate-500"><p>No referral ledger entries found</p></div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Date</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Doctor</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Vendor</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Type</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Order</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Description</th>
                              <th className="text-right py-3 px-3 text-xs font-semibold text-slate-600">Commission</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminLedgerEntries.map(entry => (
                              <tr key={entry.id} className="border-b hover:bg-slate-50">
                                <td className="py-2.5 px-3 text-xs text-slate-500">{entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-IN') : '-'}</td>
                                <td className="py-2.5 px-3">
                                  <p className="text-sm font-medium">{entry.doctor_name || '-'}</p>
                                  <p className="text-[10px] text-slate-400">{entry.doctor_specialty || ''}</p>
                                </td>
                                <td className="py-2.5 px-3">
                                  <p className="text-sm">{entry.source_name || (entry.vendor_names || []).join(', ') || '-'}</p>
                                  <p className="text-[10px] text-slate-400">{entry.vendor_type || ''}</p>
                                </td>
                                <td className="py-2.5 px-3">
                                  <Badge className={entry.source_type === 'patient_order' ? 'bg-blue-100 text-blue-700' : entry.source_type === 'b2b_order' ? 'bg-teal-100 text-teal-700' : 'bg-violet-100 text-violet-700'} variant="outline">
                                    {entry.source_type === 'patient_order' ? 'Patient' : entry.source_type === 'b2b_order' ? 'B2B' : 'Walk-in'}
                                  </Badge>
                                </td>
                                <td className="py-2.5 px-3 text-xs text-slate-600">{entry.order_number || '-'}</td>
                                <td className="py-2.5 px-3 text-xs text-slate-500 max-w-[200px] truncate">{entry.description || '-'}</td>
                                <td className="py-2.5 px-3 text-right text-sm font-bold text-amber-700">₹{(entry.charge_amount || 0).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination */}
                      {adminLedgerTotal > 30 && (
                        <div className="flex items-center justify-between mt-4">
                          <p className="text-xs text-slate-500">Showing {((adminLedgerPage - 1) * 30) + 1}-{Math.min(adminLedgerPage * 30, adminLedgerTotal)} of {adminLedgerTotal}</p>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={adminLedgerPage <= 1} onClick={() => fetchAdminReferralLedger(adminLedgerPage - 1)}>Prev</Button>
                            <Button variant="outline" size="sm" disabled={adminLedgerPage * 30 >= adminLedgerTotal} onClick={() => fetchAdminReferralLedger(adminLedgerPage + 1)}>Next</Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Doctor-Vendor Commission Rates */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Percent className="w-5 h-5 text-teal-600" />
                        Doctor-Vendor Referral Rates
                      </CardTitle>
                      <CardDescription>
                        Set custom referral rates for specific doctor-vendor pairs. These override the global default.
                      </CardDescription>
                    </div>
                    <Button onClick={() => { 
                      setDoctorCommissionForm({ doctor_id: '', vendor_id: '', commission_percent: globalDefaultCommission }); 
                      setShowDoctorCommissionDialog(true); 
                    }}>
                      <Plus className="w-4 h-4 mr-1" /> Add Referral Rate
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by doctor or vendor name..."
                        value={doctorCommissionFilters.search}
                        onChange={(e) => setDoctorCommissionFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                        data-testid="commission-search"
                      />
                    </div>
                    <Select 
                      value={doctorCommissionFilters.vendor_type || 'all'} 
                      onValueChange={(v) => setDoctorCommissionFilters(prev => ({ ...prev, vendor_type: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Vendor Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="lab">Lab</SelectItem>
                        <SelectItem value="pharma">Pharma</SelectItem>
                        <SelectItem value="inventory">Inventory</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={fetchDoctorVendorCommissions}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  </div>

                  {loadingDoctorCommissions ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Doctor</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Vendor</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Commission %</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorVendorCommissions
                            .filter(c => {
                              const matchesSearch = !doctorCommissionFilters.search || 
                                (c.doctor_name || '').toLowerCase().includes(doctorCommissionFilters.search.toLowerCase()) ||
                                (c.vendor_name || '').toLowerCase().includes(doctorCommissionFilters.search.toLowerCase()) ||
                                (c.clinic_name || '').toLowerCase().includes(doctorCommissionFilters.search.toLowerCase());
                              const matchesType = !doctorCommissionFilters.vendor_type || c.vendor_type === doctorCommissionFilters.vendor_type;
                              return matchesSearch && matchesType;
                            })
                            .map((config) => (
                            <tr key={config.id} className="border-b hover:bg-slate-50" data-testid={`commission-row-${config.id}`}>
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium">{config.doctor_name}</p>
                                  <p className="text-xs text-slate-500">{config.clinic_name}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4">{config.vendor_name}</td>
                              <td className="py-3 px-4">
                                <Badge className={config.vendor_type === 'lab' ? 'bg-cyan-100 text-cyan-700' : config.vendor_type === 'pharma' ? 'bg-pink-100 text-pink-700' : 'bg-orange-100 text-orange-700'}>
                                  {config.vendor_type}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <span className="font-bold text-teal-600">{config.commission_percent}%</span>
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={config.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {config.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => {
                                      setDoctorCommissionForm({
                                        doctor_id: config.doctor_id,
                                        vendor_id: config.vendor_id,
                                        commission_percent: config.commission_percent
                                      });
                                      setShowDoctorCommissionDialog(true);
                                    }}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => deleteDoctorVendorCommission(config.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {doctorVendorCommissions.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          <Percent className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                          <p>No custom commission rates set yet.</p>
                          <p className="text-sm">All doctors will earn {globalDefaultCommission}% commission on patient orders.</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Doctors by Commission */}
              {commissionEarningsReport?.top_doctors?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Top Doctors by Commission Earned
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {commissionEarningsReport.top_doctors.map((doctor, index) => (
                        <div key={doctor._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-orange-600' : 'bg-slate-300'}`}>
                              {index + 1}
                            </div>
                            <span className="font-medium">{doctor.doctor_name || 'Unknown Doctor'}</span>
                          </div>
                          <span className="font-bold text-green-600">₹{(doctor.total_commission || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="patients">
            <Tabs defaultValue="clinic-patients" className="w-full">
              <TabsList className="mb-4 flex-wrap h-auto">
                <TabsTrigger value="clinic-patients">Clinic Patients</TabsTrigger>
                <TabsTrigger value="portal-patients" onClick={fetchPortalPatients}>Patient Portal</TabsTrigger>
                <TabsTrigger value="patient-wallets" onClick={fetchPatientTopups}>Patient Wallets</TabsTrigger>
                <TabsTrigger value="patient-subscriptions">Patient Subscriptions</TabsTrigger>
              </TabsList>
              
              {/* Clinic Patients Tab */}
              <TabsContent value="clinic-patients">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" /> All Platform Patients
                    </CardTitle>
                    <CardDescription>View all patients registered across all clinics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search patients by name or phone..."
                          value={patientSearch}
                          onChange={(e) => setPatientSearch(e.target.value)}
                          className="pl-10"
                          data-testid="patient-search"
                        />
                      </div>
                    </div>
                    {selectedClinicPatientIds.length > 0 && (
                      <div className="mb-3 flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg" data-testid="clinic-patient-bulk-bar">
                        <span className="text-sm font-medium text-red-700">{selectedClinicPatientIds.length} selected</span>
                        <Button variant="destructive" size="sm" onClick={() => setDeleteConfirm({ type: 'clinic-patient', ids: selectedClinicPatientIds, names: filteredClinicPatients.filter(p => selectedClinicPatientIds.includes(p.id)).map(p => p.name) })} data-testid="bulk-delete-clinic-patients-btn">
                          <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
                        </Button>
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 w-8">
                              <input type="checkbox" checked={filteredClinicPatients.length > 0 && selectedClinicPatientIds.length === filteredClinicPatients.length} onChange={() => setSelectedClinicPatientIds(prev => prev.length === filteredClinicPatients.length ? [] : filteredClinicPatients.map(p => p.id))} data-testid="select-all-clinic-patients-checkbox" />
                            </th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Patient</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Contact</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Clinic</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Doctor</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Registered</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredClinicPatients
                            .map((patient) => (
                            <tr key={patient.id} className="border-b hover:bg-slate-50" data-testid={`patient-row-${patient.id}`}>
                              <td className="py-3 px-4">
                                <input type="checkbox" checked={selectedClinicPatientIds.includes(patient.id)} onChange={() => setSelectedClinicPatientIds(prev => prev.includes(patient.id) ? prev.filter(x => x !== patient.id) : [...prev, patient.id])} data-testid={`select-clinic-patient-${patient.id}`} />
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-teal-600" />
                                  </div>
                                  <div>
                                    <span className="font-medium">{patient.name}</span>
                                    {patient.age && <span className="text-sm text-slate-500 ml-2">({patient.age} yrs, {patient.gender})</span>}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <p className="text-sm">{patient.phone || '-'}</p>
                                <p className="text-xs text-slate-500">{patient.email || '-'}</p>
                              </td>
                              <td className="py-3 px-4">{patient.clinic_name || '-'}</td>
                              <td className="py-3 px-4">{patient.doctor_name || '-'}</td>
                              <td className="py-3 px-4 text-sm text-slate-500">
                                {patient.created_at ? new Date(patient.created_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => setDeleteConfirm({ type: 'clinic-patient', ids: [patient.id], names: [patient.name] })}
                                  data-testid={`delete-clinic-patient-${patient.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredClinicPatients.length === 0 && (
                        <p className="text-center py-8 text-slate-500">No patients found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Patient Portal Tab */}
              <TabsContent value="portal-patients">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <User className="w-5 h-5 text-rose-500" /> Patient Portal Users
                        </CardTitle>
                        <CardDescription>
                          {portalPatientsTotal} patients registered on the Patient Portal
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Search by name, email, phone..."
                            value={portalPatientSearch}
                            onChange={(e) => setPortalPatientSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && fetchPortalPatients()}
                            className="pl-9 w-64"
                          />
                        </div>
                        <Button variant="outline" size="sm" onClick={fetchPortalPatients} disabled={loadingPortalPatients}>
                          <RefreshCw className={`w-4 h-4 mr-2 ${loadingPortalPatients ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedPatientIds.length > 0 && (
                      <div className="mb-3 flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg" data-testid="patient-bulk-bar">
                        <span className="text-sm font-medium text-red-700">{selectedPatientIds.length} selected</span>
                        <Button variant="destructive" size="sm" onClick={() => setDeleteConfirm({ type: 'patient', ids: selectedPatientIds, names: portalPatients.filter(p => selectedPatientIds.includes(p.id)).map(p => p.name) })} data-testid="bulk-delete-patients-btn">
                          <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
                        </Button>
                      </div>
                    )}
                    {loadingPortalPatients ? (
                      <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-rose-500" />
                        <p className="text-slate-500 mt-2">Loading patients...</p>
                      </div>
                    ) : portalPatients.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <User className="w-12 h-12 mx-auto mb-4 text-rose-200" />
                        <p>No patients registered yet</p>
                        <p className="text-sm mt-2">Patients will appear here when they register on the Patient Portal</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-8">
                                <input type="checkbox" checked={portalPatients.length > 0 && selectedPatientIds.length === portalPatients.length} onChange={() => setSelectedPatientIds(prev => prev.length === portalPatients.length ? [] : portalPatients.map(p => p.id))} data-testid="select-all-patients-checkbox" />
                              </TableHead>
                              <TableHead>Patient</TableHead>
                              <TableHead>Contact</TableHead>
                              <TableHead>Registered</TableHead>
                              <TableHead>Wallet</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {portalPatients.map((patient) => (
                              <TableRow key={patient.id}>
                                <TableCell>
                                  <input type="checkbox" checked={selectedPatientIds.includes(patient.id)} onChange={() => setSelectedPatientIds(prev => prev.includes(patient.id) ? prev.filter(x => x !== patient.id) : [...prev, patient.id])} data-testid={`select-patient-${patient.id}`} />
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium">{patient.name}</div>
                                  <div className="text-xs text-slate-500">
                                    {patient.gender && <span className="capitalize">{patient.gender}</span>}
                                    {patient.date_of_birth && <span> • DOB: {patient.date_of_birth}</span>}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="text-sm">{patient.email}</div>
                                  <div className="text-xs text-slate-500">{patient.phone}</div>
                                </TableCell>
                                <TableCell>
                                  <div className="text-sm">
                                    {new Date(patient.created_at).toLocaleDateString()}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {new Date(patient.created_at).toLocaleTimeString()}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="font-medium text-green-600">
                                    ₹{(patient.wallet_balance || 0).toLocaleString()}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant={patient.status === 'active' ? 'default' : 'destructive'}
                                    className={patient.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                                  >
                                    {patient.status || 'active'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`patient-actions-${patient.id}`}>
                                        <MoreHorizontal className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => { setEditPatientDialog(patient); setEditPatientForm({ name: patient.name || '', email: patient.email || '', phone: patient.phone || '', gender: patient.gender || '', date_of_birth: patient.date_of_birth || '', blood_group: patient.blood_group || '' }); }}>
                                        <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      {patient.status === 'active' ? (
                                        <DropdownMenuItem onClick={() => handlePortalPatientStatus(patient.id, 'suspended')} className="text-amber-600">
                                          <Ban className="w-4 h-4 mr-2" /> Suspend
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem onClick={() => handlePortalPatientStatus(patient.id, 'active')} className="text-green-600">
                                          <CheckCircle className="w-4 h-4 mr-2" /> Activate
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => setDeleteConfirm({ type: 'patient', ids: [patient.id], names: [patient.name] })} className="text-red-600">
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete Account
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Patient Wallets Tab */}
              <TabsContent value="patient-wallets">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-green-500" /> Patient Wallet Top-ups
                          </CardTitle>
                          <CardDescription>Approve or reject patient wallet top-up requests</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={fetchPatientTopups} disabled={loadingPatientTopups}>
                          <RefreshCw className={`w-4 h-4 mr-2 ${loadingPatientTopups ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Input placeholder="Search patient..." value={patientTopupFilter.search} onChange={(e) => setPatientTopupFilter(f => ({...f, search: e.target.value}))} className="w-40 h-8 text-xs" data-testid="patient-topup-search" />
                        <Select value={patientTopupFilter.status} onValueChange={(v) => { setPatientTopupFilter(f => ({ ...f, status: v })); }}>
                          <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="h-8" onClick={fetchPatientTopups}><Search className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingPatientTopups ? (
                      <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-green-500" />
                        <p className="text-slate-500 mt-2">Loading patient top-up requests...</p>
                      </div>
                    ) : pendingPatientTopups.filter(r => !patientTopupFilter.search || (r.patient_name || '').toLowerCase().includes(patientTopupFilter.search.toLowerCase())).length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Wallet className="w-12 h-12 mx-auto mb-4 text-green-200" />
                        <p>No patient top-up requests found</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingPatientTopups.filter(r => !patientTopupFilter.search || (r.patient_name || '').toLowerCase().includes(patientTopupFilter.search.toLowerCase())).map((req) => (
                          <div key={req.id || req._id} className="p-4 bg-slate-50 rounded-lg border">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-semibold text-slate-800">{req.patient_name || 'Patient'}</p>
                                  <Badge variant="outline" className="text-xs">{req.patient_phone}</Badge>
                                </div>
                                <p className="text-2xl font-bold text-green-600 mb-2">₹{req.amount?.toLocaleString()}</p>
                                {req.utr_number && (
                                  <p className="text-sm text-slate-600">
                                    <span className="font-medium">UTR:</span> {req.utr_number}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400 mt-1">
                                  {new Date(req.created_at).toLocaleString()}
                                </p>
                              </div>
                              
                              {/* Screenshot Preview */}
                              {req.screenshot_url && (
                                <div className="flex flex-col items-center">
                                  <p className="text-xs text-slate-500 mb-1">Payment Proof</p>
                                  <a 
                                    href={`${process.env.REACT_APP_BACKEND_URL}${req.screenshot_url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="block w-20 h-20 border rounded-lg overflow-hidden bg-white hover:opacity-80 transition-opacity"
                                  >
                                    <img 
                                      src={`${process.env.REACT_APP_BACKEND_URL}${req.screenshot_url}`} 
                                      alt="Payment Screenshot" 
                                      className="w-full h-full object-cover"
                                    />
                                  </a>
                                </div>
                              )}
                              
                              {/* Action Buttons */}
                              <div className="flex flex-col gap-2">
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handlePatientTopupAction(req.id || req._id, 'approve')}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handlePatientTopupAction(req.id || req._id, 'reject')}
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Patient Subscriptions Tab */}
              <TabsContent value="patient-subscriptions">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500" /> Patient Subscriptions
                    </CardTitle>
                    <CardDescription>Manage patient subscription plans and promo codes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-12 text-slate-500">
                      <Star className="w-12 h-12 mx-auto mb-4 text-amber-200" />
                      <p>Patient subscription management will be displayed here</p>
                      <p className="text-sm mt-2">Endpoint: <code className="bg-slate-100 px-2 py-1 rounded">GET /api/patient-subscription/admin/subscriptions</code></p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Clinic Profitability Tab */}
          <TabsContent value="profitability">
            <div className="space-y-6">
              {/* Filters Section */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Clinic Profitability Analytics</h2>
                <div className="flex gap-2 flex-wrap items-center">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Search clinic or doctor..."
                      value={profitabilitySearch}
                      onChange={e => setProfitabilitySearch(e.target.value)}
                      className="pl-10 w-[200px]"
                      data-testid="profitability-search"
                    />
                  </div>
                  
                  {/* Date Selection */}
                  {!useProfitabilityDateRange ? (
                    <Select value={profitabilityMonth} onValueChange={setProfitabilityMonth}>
                      <SelectTrigger className="w-[160px]" data-testid="profitability-month-selector">
                        <Calendar className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {generateMonthOptions().map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="date" 
                        value={profitabilityDateRange.start} 
                        onChange={e => setProfitabilityDateRange({...profitabilityDateRange, start: e.target.value})}
                        className="w-[130px]"
                        data-testid="profitability-date-start"
                      />
                      <span className="text-slate-400 text-sm">to</span>
                      <Input 
                        type="date" 
                        value={profitabilityDateRange.end} 
                        onChange={e => setProfitabilityDateRange({...profitabilityDateRange, end: e.target.value})}
                        className="w-[130px]"
                        data-testid="profitability-date-end"
                      />
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setUseProfitabilityDateRange(!useProfitabilityDateRange)}
                    data-testid="profitability-toggle-date-range"
                  >
                    {useProfitabilityDateRange ? 'Month' : 'Range'}
                  </Button>
                  
                  <Button onClick={handleProfitabilitySearch} data-testid="profitability-search-btn">
                    <Search className="w-4 h-4 mr-2" /> Search
                  </Button>
                </div>
              </div>

              {loadingProfitability ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : profitabilityData ? (
                <>
                  {/* Platform Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Platform Revenue</p>
                            <p className="text-2xl font-bold text-green-600">₹{profitabilityData.platform_summary?.total_revenue?.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Platform Expenses</p>
                            <p className="text-2xl font-bold text-red-600">₹{profitabilityData.platform_summary?.total_expenses?.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                            <TrendingDown className="w-6 h-6 text-red-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className={`border-l-4 ${profitabilityData.platform_summary?.net_profit >= 0 ? 'border-l-teal-500' : 'border-l-orange-500'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Net Profit</p>
                            <p className={`text-2xl font-bold ${profitabilityData.platform_summary?.net_profit >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
                              {profitabilityData.platform_summary?.net_profit >= 0 ? '+' : ''}₹{profitabilityData.platform_summary?.net_profit?.toLocaleString()}
                            </p>
                          </div>
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${profitabilityData.platform_summary?.net_profit >= 0 ? 'bg-teal-100' : 'bg-orange-100'}`}>
                            <Wallet className={`w-6 h-6 ${profitabilityData.platform_summary?.net_profit >= 0 ? 'text-teal-600' : 'text-orange-600'}`} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Active Clinics</p>
                            <p className="text-2xl font-bold text-blue-600">{profitabilityData.platform_summary?.clinic_count}</p>
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Clinic-wise Profitability Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Clinic-wise Profitability</CardTitle>
                      <CardDescription>Revenue vs Expenses for each clinic</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left p-3 font-medium">Clinic</th>
                              <th className="text-left p-3 font-medium">Doctor</th>
                              <th className="text-right p-3 font-medium">Revenue</th>
                              <th className="text-right p-3 font-medium">Expenses</th>
                              <th className="text-right p-3 font-medium">Net Profit</th>
                              <th className="text-right p-3 font-medium">Margin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {profitabilityData.clinics?.map((clinic, idx) => (
                              <tr key={clinic.doctor_id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                                <td className="p-3 font-medium">{clinic.clinic_name || 'N/A'}</td>
                                <td className="p-3 text-slate-600">{clinic.doctor_name}</td>
                                <td className="p-3 text-right text-green-600 font-medium">₹{clinic.total_revenue?.toLocaleString()}</td>
                                <td className="p-3 text-right text-red-600 font-medium">₹{clinic.total_expenses?.toLocaleString()}</td>
                                <td className={`p-3 text-right font-bold ${clinic.net_profit >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
                                  {clinic.net_profit >= 0 ? '+' : ''}₹{clinic.net_profit?.toLocaleString()}
                                </td>
                                <td className="p-3 text-right">
                                  <Badge variant={clinic.profit_margin >= 20 ? 'default' : clinic.profit_margin >= 0 ? 'secondary' : 'destructive'}>
                                    {clinic.profit_margin}%
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                            {(!profitabilityData.clinics || profitabilityData.clinics.length === 0) && (
                              <tr>
                                <td colSpan={6} className="p-8 text-center text-slate-500">No clinic data available for this period</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Click the "Profitability" tab to load data</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Consultation Coupons Tab */}
          <TabsContent value="consult-coupons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Tag className="w-5 h-5 text-violet-600" /> Consultation Coupons
                    </CardTitle>
                    <CardDescription>Create discount coupons for online consultations</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchConsultCoupons}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                    <Button size="sm" onClick={() => { setShowCouponForm(true); fetchCouponDoctors(); }} data-testid="create-consult-coupon-btn">
                      + New Coupon
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {consultCouponsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : consultCoupons.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Tag className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No consultation coupons yet. Click "New Coupon" to create one.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {consultCoupons.map(c => (
                      <div key={c.id} className="border rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-mono font-bold text-lg">{c.code}</p>
                            <Badge className={c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {c.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                            {' · Total: '}{(c.max_uses_total || c.max_uses || 0) > 0 ? `${c.max_uses_total || c.max_uses}` : '∞'}
                            {' · Per patient: '}{(c.max_uses_per_patient || 0) > 0 ? `${c.max_uses_per_patient}` : '∞'}
                            {' · Used: '}{c.used_count || 0}
                          </p>
                          {c.description && <p className="text-xs text-slate-400 mt-1">{c.description}</p>}
                          {c.applicable_doctors && c.applicable_doctors.length > 0 && (
                            <p className="text-xs text-violet-500 mt-1">Doctors: {c.applicable_doctors.length} selected</p>
                          )}
                          {(c.valid_from || c.valid_to) && (
                            <p className="text-xs text-slate-400">
                              {c.valid_from ? `From: ${new Date(c.valid_from).toLocaleDateString()}` : ''}
                              {c.valid_to ? ` · Until: ${new Date(c.valid_to).toLocaleDateString()}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => toggleConsultCoupon(c.id, c.is_active)}>
                            {c.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500" onClick={() => deleteConsultCoupon(c.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Create Coupon Dialog */}
            <Dialog open={showCouponForm} onOpenChange={setShowCouponForm}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Consultation Coupon</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Coupon Code *</Label>
                    <Input value={couponForm.code} onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                      placeholder="e.g. IMMUSKI100" data-testid="coupon-code-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type</Label>
                      <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm({...couponForm, discount_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="amount">Fixed Amount (₹)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Discount Value *</Label>
                      <Input type="number" value={couponForm.discount_value}
                        onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                        placeholder={couponForm.discount_type === 'percentage' ? 'e.g. 50' : 'e.g. 200'} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Max Uses (All Patients)</Label>
                      <Input type="number" value={couponForm.max_uses_total}
                        onChange={(e) => setCouponForm({...couponForm, max_uses_total: e.target.value})} placeholder="0 = unlimited" />
                    </div>
                    <div>
                      <Label>Max Uses / Patient</Label>
                      <Input type="number" value={couponForm.max_uses_per_patient}
                        onChange={(e) => setCouponForm({...couponForm, max_uses_per_patient: e.target.value})} placeholder="0 = unlimited" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valid From</Label>
                      <Input type="date" value={couponForm.valid_from}
                        onChange={(e) => setCouponForm({...couponForm, valid_from: e.target.value})} />
                    </div>
                    <div>
                      <Label>Valid Until</Label>
                      <Input type="date" value={couponForm.valid_to}
                        onChange={(e) => setCouponForm({...couponForm, valid_to: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={couponForm.description}
                      onChange={(e) => setCouponForm({...couponForm, description: e.target.value})}
                      placeholder="e.g. Immuski customer free consultation" />
                  </div>
                  <div>
                    <Label>Applicable Doctors</Label>
                    <div className="flex gap-3 mt-1 mb-2">
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="radio" name="doctorScope" checked={couponDoctorScope === 'all'}
                          onChange={() => setCouponDoctorScope('all')} /> All Doctors
                      </label>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="radio" name="doctorScope" checked={couponDoctorScope === 'specific'}
                          onChange={() => setCouponDoctorScope('specific')} /> Specific Doctors
                      </label>
                    </div>
                    {couponDoctorScope === 'specific' && (
                      <div className="border rounded-lg p-2 max-h-[150px] overflow-y-auto space-y-1">
                        {couponDoctors.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-2">No doctors found</p>
                        ) : couponDoctors.map(d => (
                          <label key={d.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                            <input type="checkbox"
                              checked={couponForm.applicable_doctors.includes(d.id)}
                              onChange={(e) => {
                                const ids = e.target.checked
                                  ? [...couponForm.applicable_doctors, d.id]
                                  : couponForm.applicable_doctors.filter(x => x !== d.id);
                                setCouponForm({...couponForm, applicable_doctors: ids});
                              }} />
                            <span className="font-medium">{d.name}</span>
                            <span className="text-xs text-slate-400">{d.specialty}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCouponForm(false)}>Cancel</Button>
                  <Button onClick={createConsultCoupon} disabled={!couponForm.code || !couponForm.discount_value}
                    data-testid="save-coupon-btn">Create Coupon</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Online Consultations Tab */}
          <TabsContent value="online-consultations">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2"><Video className="w-5 h-5 text-teal-600" /> Online Consultations</CardTitle>
                    <CardDescription>All booked consultations with coupon and payment details</CardDescription>
                  </div>
                  <div className="flex gap-2 items-center">
                    {adminConsultSummary && (
                      <div className="flex gap-3 text-xs">
                        <span className="bg-slate-100 px-2 py-1 rounded">Total: {adminConsultSummary.total_consultations}</span>
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Paid: {adminConsultSummary.paid}</span>
                        <span className="bg-violet-100 text-violet-700 px-2 py-1 rounded">With Coupon: {adminConsultSummary.with_coupon}</span>
                      </div>
                    )}
                    <Button variant="outline" size="sm" onClick={() => fetchAdminConsultations(1)}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[180px]">
                      <Label className="text-xs">Search</Label>
                      <Input placeholder="Patient, doctor, coupon..." value={adminConsultFilters.search}
                        onChange={(e) => setAdminConsultFilters(p => ({...p, search: e.target.value}))} className="bg-white h-9" />
                    </div>
                    <div className="w-[120px]">
                      <Label className="text-xs">Status</Label>
                      <Select value={adminConsultFilters.status} onValueChange={(v) => setAdminConsultFilters(p => ({...p, status: v}))}>
                        <SelectTrigger className="bg-white h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="requested">Requested</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-[120px]">
                      <Label className="text-xs">Coupon</Label>
                      <Select value={adminConsultFilters.has_coupon} onValueChange={(v) => setAdminConsultFilters(p => ({...p, has_coupon: v}))}>
                        <SelectTrigger className="bg-white h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="yes">With Coupon</SelectItem>
                          <SelectItem value="no">No Coupon</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-[120px]">
                      <Label className="text-xs">From</Label>
                      <Input type="date" className="bg-white h-9" value={adminConsultFilters.date_from}
                        onChange={(e) => setAdminConsultFilters(p => ({...p, date_from: e.target.value}))} />
                    </div>
                    <div className="w-[120px]">
                      <Label className="text-xs">To</Label>
                      <Input type="date" className="bg-white h-9" value={adminConsultFilters.date_to}
                        onChange={(e) => setAdminConsultFilters(p => ({...p, date_to: e.target.value}))} />
                    </div>
                    <Button size="sm" onClick={() => fetchAdminConsultations(1)}>Filter</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setAdminConsultFilters({ search: '', status: 'all', has_coupon: 'all', date_from: '', date_to: '' }); fetchAdminConsultations(1); }}>Reset</Button>
                  </div>
                </div>

                {adminConsultLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : adminConsultations.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Video className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No consultations found</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="text-left py-2 px-3">Patient</th>
                            <th className="text-left py-2 px-3">Doctor</th>
                            <th className="text-left py-2 px-3">Date/Time</th>
                            <th className="text-left py-2 px-3">Reason</th>
                            <th className="text-right py-2 px-3">Fee</th>
                            <th className="text-center py-2 px-3">Coupon</th>
                            <th className="text-right py-2 px-3">Paid</th>
                            <th className="text-center py-2 px-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {adminConsultations.map(c => (
                            <tr key={c.id} className="hover:bg-slate-50">
                              <td className="py-2 px-3">
                                <p className="font-medium">{c.patient_name}</p>
                                <p className="text-xs text-slate-400">{c.patient_phone}</p>
                              </td>
                              <td className="py-2 px-3">
                                <p className="font-medium">{c.doctor_name}</p>
                                <p className="text-xs text-slate-400">{c.doctor_specialty}</p>
                              </td>
                              <td className="py-2 px-3">
                                <p>{c.preferred_date}</p>
                                <p className="text-xs text-slate-400">{c.preferred_time || c.scheduled_time || '-'}</p>
                              </td>
                              <td className="py-2 px-3 max-w-[150px] truncate">{c.reason}</td>
                              <td className="py-2 px-3 text-right">₹{c.fee}</td>
                              <td className="py-2 px-3 text-center">
                                {c.coupon_code ? (
                                  <div>
                                    <Badge className="bg-violet-100 text-violet-700">{c.coupon_code}</Badge>
                                    <p className="text-xs text-green-600 mt-0.5">-₹{c.coupon_discount}</p>
                                  </div>
                                ) : (<span className="text-slate-300">—</span>)}
                              </td>
                              <td className="py-2 px-3 text-right font-medium">{c.amount_paid != null ? `₹${c.amount_paid}` : c.final_fee != null ? `₹${c.final_fee}` : `₹${c.fee}`}</td>
                              <td className="py-2 px-3 text-center">
                                <Badge className={
                                  c.status === 'confirmed' || c.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  c.status === 'cancelled' || c.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  c.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                  'bg-amber-100 text-amber-700'
                                }>{c.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {adminConsultTotal > 50 && (
                      <div className="flex justify-between items-center mt-4">
                        <p className="text-sm text-slate-500">Showing {adminConsultations.length} of {adminConsultTotal}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={adminConsultPage <= 1} onClick={() => fetchAdminConsultations(adminConsultPage - 1)}>Previous</Button>
                          <Button variant="outline" size="sm" onClick={() => fetchAdminConsultations(adminConsultPage + 1)}>Next</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support-tickets">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Total', value: adminTicketStats.total, color: 'text-slate-700 bg-slate-50' },
                  { label: 'Open', value: adminTicketStats.open, color: 'text-blue-700 bg-blue-50' },
                  { label: 'In Progress', value: adminTicketStats.in_progress, color: 'text-amber-700 bg-amber-50' },
                  { label: 'Resolved', value: adminTicketStats.resolved, color: 'text-green-700 bg-green-50' },
                  { label: 'By Type', value: `D:${adminTicketStats.by_type?.doctor || 0} V:${adminTicketStats.by_type?.vendor || 0} P:${adminTicketStats.by_type?.patient || 0}`, color: 'text-purple-700 bg-purple-50' }
                ].map(s => (
                  <Card key={s.label}>
                    <CardContent className={`p-4 ${s.color} rounded-lg`}>
                      <p className="text-xs opacity-70">{s.label}</p>
                      <p className="text-xl font-bold">{s.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Filters */}
              <div className="flex gap-3 flex-wrap items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="admin-ticket-search"
                    value={ticketFilter.search}
                    onChange={e => { setTicketFilter(p => ({...p, search: e.target.value})); }}
                    onKeyDown={e => { if (e.key === 'Enter') fetchAdminTickets(); }}
                    placeholder="Search tickets..."
                    className="pl-9 h-9"
                  />
                </div>
                <Select value={ticketFilter.status} onValueChange={v => { setTicketFilter(p => ({...p, status: v})); }}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="admin-ticket-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ticketFilter.user_type} onValueChange={v => { setTicketFilter(p => ({...p, user_type: v})); }}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="admin-ticket-type-filter"><SelectValue placeholder="User Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="patient">Patient</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ticketFilter.category} onValueChange={v => { setTicketFilter(p => ({...p, category: v})); }}>
                  <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="marketplace">Marketplace</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={fetchAdminTickets} data-testid="admin-ticket-apply-filter">
                  <Filter className="h-4 w-4 mr-1" /> Apply
                </Button>
              </div>

              {/* Tickets Table */}
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminTickets.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No tickets found</TableCell></TableRow>
                    )}
                    {adminTickets.map(ticket => (
                      <TableRow key={ticket.id} data-testid={`admin-ticket-${ticket.ticket_number}`}>
                        <TableCell className="font-mono text-xs">{ticket.ticket_number}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">{ticket.user_name || 'N/A'}</p>
                            <Badge variant="outline" className="text-xs">{ticket.user_type}</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm">{ticket.subject}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs">{ticket.category}</Badge></TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' : ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>
                            {ticket.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={ticket.status === 'open' ? 'default' : ticket.status === 'resolved' ? 'outline' : 'secondary'} className="text-xs">
                            {ticket.status?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(ticket.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedTicket(ticket); setAdminReplyText(''); setAdminReplyStatus(''); }} data-testid={`view-ticket-${ticket.ticket_number}`}>
                            <Eye className="h-3 w-3 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          </TabsContent>

          {/* Push Notifications Tab */}
          <TabsContent value="push-notifications">
            <PushNotificationsPanel />
          </TabsContent>

          {/* Admin Profile Tab */}
          <TabsContent value="profile">
            <div className="space-y-6">
              {/* Admin Profile Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <UserCircle className="w-5 h-5 text-purple-600" /> Admin Profile
                      </CardTitle>
                      <CardDescription>Manage your admin account and platform logo</CardDescription>
                    </div>
                    {!editingProfile ? (
                      <Button variant="outline" onClick={() => setEditingProfile(true)} data-testid="edit-admin-profile-btn">
                        <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => { setEditingProfile(false); setProfileForm({ name: adminProfile?.name || '', email: adminProfile?.email || '', phone: adminProfile?.phone || '' }); }}>
                          Cancel
                        </Button>
                        <Button onClick={saveAdminProfile} disabled={savingProfile} data-testid="save-admin-profile-btn">
                          {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Changes
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Logo Upload */}
                  <div className="flex items-center gap-6 pb-4 border-b">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                        {adminProfile?.logo_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${adminProfile.logo_url}`} alt="Admin Logo" className="w-full h-full object-cover" />
                        ) : (
                          <Shield className="w-10 h-10 text-slate-400" />
                        )}
                      </div>
                      <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-700 transition-colors shadow-lg">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) uploadAdminLogo(file);
                          }}
                        />
                        <Image className="w-4 h-4 text-white" />
                      </label>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{adminProfile?.name || 'Platform Admin'}</h3>
                      <p className="text-sm text-slate-500">Super Administrator</p>
                      <p className="text-xs text-purple-600 mt-1">Click the icon to upload platform logo</p>
                    </div>
                  </div>

                  {/* Profile Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500 text-xs">Full Name</Label>
                      {editingProfile ? (
                        <Input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} className="mt-1" />
                      ) : (
                        <p className="font-medium text-slate-900">{adminProfile?.name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Email</Label>
                      {editingProfile ? (
                        <Input value={profileForm.email} onChange={(e) => setProfileForm({...profileForm, email: e.target.value})} className="mt-1" type="email" />
                      ) : (
                        <p className="font-medium text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {adminProfile?.email || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Phone</Label>
                      {editingProfile ? (
                        <Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} className="mt-1" />
                      ) : (
                        <p className="font-medium text-slate-900 flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {adminProfile?.phone || '-'}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Support Contacts Configuration */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <HelpCircle className="w-5 h-5 text-blue-600" /> Support Contact Settings
                      </CardTitle>
                      <CardDescription>Configure support contact details shown in Doctor and Vendor portals</CardDescription>
                    </div>
                    <Button onClick={saveSupportContacts} disabled={savingSupport} data-testid="save-support-contacts-btn">
                      {savingSupport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Support Settings
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Doctor Support Section */}
                  <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <h4 className="font-semibold text-teal-800 mb-4 flex items-center gap-2">
                      <User className="w-4 h-4" /> Doctor Portal Support
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-teal-700">Support Email</Label>
                        <Input
                          value={supportContacts.doctor_support?.support_email || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, doctor_support: {...supportContacts.doctor_support, support_email: e.target.value}})}
                          placeholder="support@1clin.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-teal-700">Support Phone</Label>
                        <Input
                          value={supportContacts.doctor_support?.support_phone || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, doctor_support: {...supportContacts.doctor_support, support_phone: e.target.value}})}
                          placeholder="+91 98765 43210"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-teal-700">WhatsApp Number</Label>
                        <Input
                          value={supportContacts.doctor_support?.whatsapp_number || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, doctor_support: {...supportContacts.doctor_support, whatsapp_number: e.target.value}})}
                          placeholder="+91 98765 43210"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-teal-700">Business Hours</Label>
                        <Input
                          value={supportContacts.doctor_support?.business_hours || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, doctor_support: {...supportContacts.doctor_support, business_hours: e.target.value}})}
                          placeholder="Mon-Fri 9AM-6PM IST"
                          className="mt-1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-teal-700">Office Address</Label>
                        <Textarea
                          value={supportContacts.doctor_support?.office_address || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, doctor_support: {...supportContacts.doctor_support, office_address: e.target.value}})}
                          placeholder="Full office address"
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-teal-700">Additional Info</Label>
                        <Textarea
                          value={supportContacts.doctor_support?.additional_info || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, doctor_support: {...supportContacts.doctor_support, additional_info: e.target.value}})}
                          placeholder="Any additional support information..."
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Vendor Support Section */}
                  <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                    <h4 className="font-semibold text-rose-800 mb-4 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Vendor Portal Support
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-rose-700">Support Email</Label>
                        <Input
                          value={supportContacts.vendor_support?.support_email || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, vendor_support: {...supportContacts.vendor_support, support_email: e.target.value}})}
                          placeholder="vendor-support@1clin.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-rose-700">Support Phone</Label>
                        <Input
                          value={supportContacts.vendor_support?.support_phone || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, vendor_support: {...supportContacts.vendor_support, support_phone: e.target.value}})}
                          placeholder="+91 98765 43210"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-rose-700">WhatsApp Number</Label>
                        <Input
                          value={supportContacts.vendor_support?.whatsapp_number || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, vendor_support: {...supportContacts.vendor_support, whatsapp_number: e.target.value}})}
                          placeholder="+91 98765 43210"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-rose-700">Business Hours</Label>
                        <Input
                          value={supportContacts.vendor_support?.business_hours || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, vendor_support: {...supportContacts.vendor_support, business_hours: e.target.value}})}
                          placeholder="Mon-Fri 9AM-6PM IST"
                          className="mt-1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-rose-700">Office Address</Label>
                        <Textarea
                          value={supportContacts.vendor_support?.office_address || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, vendor_support: {...supportContacts.vendor_support, office_address: e.target.value}})}
                          placeholder="Full office address"
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-rose-700">Additional Info</Label>
                        <Textarea
                          value={supportContacts.vendor_support?.additional_info || ''}
                          onChange={(e) => setSupportContacts({...supportContacts, vendor_support: {...supportContacts.vendor_support, additional_info: e.target.value}})}
                          placeholder="Any additional support information..."
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Chemist Orders Tab */}
          <TabsContent value="chemist-orders">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Pill className="w-5 h-5 text-emerald-600" /> Chemist B2B Orders
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {adminChemistOrdersLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : adminChemistOrders.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No chemist orders found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-slate-50">
                        <th className="text-left p-2">Order #</th>
                        <th className="text-left p-2">Chemist (Buyer)</th>
                        <th className="text-left p-2">Vendor</th>
                        <th className="text-right p-2">Amount</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Date</th>
                      </tr></thead>
                      <tbody>
                        {adminChemistOrders.map(order => (
                          <tr key={order.id || order.order_number} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-mono text-xs">{order.order_number}</td>
                            <td className="p-2">{order.doctor_name || order.clinic_name}</td>
                            <td className="p-2">{order.vendor_name}</td>
                            <td className="p-2 text-right font-medium">₹{order.total?.toLocaleString()}</td>
                            <td className="p-2"><Badge className={`capitalize text-xs ${order.status === 'delivered' ? 'bg-green-100 text-green-700' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{order.status}</Badge></td>
                            <td className="p-2 text-xs">{new Date(order.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="space-y-6">
              {/* Commission Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600 font-medium">Total Commission Earned</p>
                    <p className="text-2xl font-bold text-green-700">₹{commissionData?.total_commission?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total Orders</p>
                    <p className="text-2xl font-bold text-slate-800">{commissionData?.total_orders || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total GMV (MRP)</p>
                    <p className="text-2xl font-bold text-slate-800">₹{commissionData?.total_mrp?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Wallet Circulation</p>
                    <p className="text-2xl font-bold text-teal-700">₹{walletOverview?.total_points_circulation?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Commission by Vendor Type */}
              {commissionData?.by_vendor_type && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" /> Commission by Vendor Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {commissionData.by_vendor_type.map((item) => (
                        <div key={item._id} className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            {item._id === 'lab' && <FlaskConical className="w-5 h-5 text-cyan-600" />}
                            {item._id === 'pharma' && <Pill className="w-5 h-5 text-pink-600" />}
                            {item._id === 'inventory' && <Package className="w-5 h-5 text-orange-600" />}
                            <span className="font-medium capitalize">{item._id}</span>
                          </div>
                          <p className="text-xl font-bold text-green-600">₹{item.commission?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{item.orders} orders • ₹{item.mrp?.toLocaleString()} MRP</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* QR Config & Add Credits */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <QrCode className="w-5 h-5 text-amber-600" /> Payment QR
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input value={qrForm.upi_id} onChange={(e) => setQrForm({ ...qrForm, upi_id: e.target.value })} placeholder="UPI ID" />
                    <Input value={qrForm.payee_name} onChange={(e) => setQrForm({ ...qrForm, payee_name: e.target.value })} placeholder="Payee Name" />
                    {qrPreview && <img src={qrPreview} alt="QR" className="w-32 h-32 mx-auto object-contain rounded" />}
                    <label className="block text-center p-2 border-2 border-dashed rounded cursor-pointer text-sm text-slate-500">
                      <Upload className="w-4 h-4 mx-auto mb-1" /> Upload QR Image
                      <input type="file" accept="image/*" className="hidden" onChange={handleQrImageChange} />
                    </label>
                    <Button onClick={saveQrConfig} disabled={savingQr} className="w-full bg-amber-600 hover:bg-amber-700" size="sm">
                      {savingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save QR
                    </Button>
                    <div className="border-t pt-3">
                      <Button onClick={() => { fetchDoctorsList(); setShowAddCreditsDialog(true); }} variant="outline" className="w-full" size="sm">
                        <Plus className="w-4 h-4 mr-1" /> Add Credits to Doctor
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Pending Top-ups */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-600" /> Pending Top-ups ({pendingTopups.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-80 overflow-y-auto space-y-2">
                    {pendingTopups.length === 0 ? (
                      <p className="text-center text-slate-500 py-4">No pending requests</p>
                    ) : pendingTopups.map((req) => (
                      <div key={req.id} className="p-3 bg-slate-50 rounded-lg border text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{req.doctor_name}</p>
                            <p className="text-slate-500 text-xs">{req.clinic_name}</p>
                            <p className="font-bold text-teal-600">₹{req.amount?.toLocaleString()}</p>
                            {req.utr_number && <p className="text-xs text-slate-400">UTR: {req.utr_number}</p>}
                            <p className="text-xs text-slate-400">{new Date(req.created_at).toLocaleString()}</p>
                          </div>
                          <div className="flex flex-col gap-1">
                            {req.screenshot_url && <a href={req.screenshot_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600"><Eye className="w-3 h-3" /></a>}
                            <Button size="sm" className="bg-green-600 h-6 text-xs" onClick={() => handleTopupAction(req.id, 'approve')}><CheckCircle className="w-3 h-3" /></Button>
                            <Button size="sm" variant="destructive" className="h-6 text-xs" onClick={() => handleTopupAction(req.id, 'reject')}><XCircle className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Vendor Payouts removed - replaced by Wallet Redemption system */}
              </div>

              {/* Vendor Wallets Overview */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-teal-600" /> Vendor Wallets ({vendorWalletsList.length})
                    </CardTitle>
                    <div className="flex gap-2 flex-wrap">
                      <Input placeholder="Search vendor..." value={vendorWalletSearch} onChange={(e) => setVendorWalletSearch(e.target.value)} className="w-48 h-8 text-xs" data-testid="vendor-wallet-search" />
                      <Select value={vendorWalletTypeFilter} onValueChange={setVendorWalletTypeFilter}>
                        <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="pharma">Pharma</SelectItem>
                          <SelectItem value="chemist">Chemist</SelectItem>
                          <SelectItem value="lab">Lab</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="max-h-60 overflow-y-auto">
                  {vendorWalletsList.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No vendor wallets created yet</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {vendorWalletsList.filter(w => {
                        if (vendorWalletSearch && !(w.vendor_name || '').toLowerCase().includes(vendorWalletSearch.toLowerCase())) return false;
                        if (vendorWalletTypeFilter !== 'all' && (w.vendor_type || '') !== vendorWalletTypeFilter) return false;
                        return true;
                      }).map((w) => (
                        <div key={w.vendor_id} className="p-3 border rounded-lg bg-white" data-testid="admin-vendor-wallet-card">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm text-slate-700">{w.vendor_name || w.vendor_id}</p>
                            {w.vendor_type && <span className="text-[10px] capitalize bg-slate-100 text-slate-600 px-1.5 rounded">{w.vendor_type}</span>}
                          </div>
                          <p className="text-lg font-bold text-teal-600">₹{(w.balance || 0).toLocaleString('en-IN')}</p>
                          <div className="flex gap-3 text-xs text-slate-400 mt-1">
                            <span>Earned: ₹{(w.total_earned || 0).toLocaleString()}</span>
                            <span>Redeemed: ₹{(w.total_redeemed || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Vendor Wallet Top-up Requests */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Plus className="w-5 h-5 text-blue-600" /> Vendor Wallet Top-up Requests ({vendorTopupRequests.length})
                      </CardTitle>
                      <Button size="sm" variant="outline" onClick={fetchVendorTopupRequests}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Input placeholder="Search vendor..." value={topupFilter.search} onChange={(e) => setTopupFilter(f => ({...f, search: e.target.value}))} className="w-40 h-8 text-xs" data-testid="topup-search" />
                      <Input type="date" value={topupFilter.date_from} onChange={(e) => setTopupFilter(f => ({...f, date_from: e.target.value}))} className="w-32 h-8 text-xs" />
                      <Input type="date" value={topupFilter.date_to} onChange={(e) => setTopupFilter(f => ({...f, date_to: e.target.value}))} className="w-32 h-8 text-xs" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="max-h-96 overflow-y-auto space-y-3">
                  {vendorTopupRequests.filter(req => {
                    if (topupFilter.search && !(req.vendor_name || '').toLowerCase().includes(topupFilter.search.toLowerCase())) return false;
                    if (topupFilter.date_from && req.created_at && new Date(req.created_at).toISOString().split('T')[0] < topupFilter.date_from) return false;
                    if (topupFilter.date_to && req.created_at && new Date(req.created_at).toISOString().split('T')[0] > topupFilter.date_to) return false;
                    return true;
                  }).length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No pending vendor top-up requests</p>
                  ) : vendorTopupRequests.filter(req => {
                    if (topupFilter.search && !(req.vendor_name || '').toLowerCase().includes(topupFilter.search.toLowerCase())) return false;
                    if (topupFilter.date_from && req.created_at && new Date(req.created_at).toISOString().split('T')[0] < topupFilter.date_from) return false;
                    if (topupFilter.date_to && req.created_at && new Date(req.created_at).toISOString().split('T')[0] > topupFilter.date_to) return false;
                    return true;
                  }).map((req) => (
                    <div key={req.id} className="p-3 bg-white rounded-lg border shadow-sm" data-testid="vendor-topup-admin-row">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-slate-800">{req.vendor_name}</p>
                            <Badge className="text-xs capitalize bg-slate-100 text-slate-600">{req.vendor_type}</Badge>
                          </div>
                          <p className="text-xl font-bold text-blue-600 mb-1">₹{req.amount?.toLocaleString()}</p>
                          {req.notes && <p className="text-xs text-slate-500">{req.notes}</p>}
                          {req.utr_number && <p className="text-xs text-slate-600 font-medium">Transaction ID: {req.utr_number}</p>}
                          {req.screenshot_url && (
                            <div className="mt-1">
                              <p className="text-xs text-slate-500 mb-1">Payment Screenshot:</p>
                              <a href={req.screenshot_url} target="_blank" rel="noopener noreferrer">
                                <img src={req.screenshot_url} alt="Payment proof" className="w-24 h-24 border rounded object-cover hover:opacity-80 transition" />
                              </a>
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-1">{req.created_at ? new Date(req.created_at).toLocaleString() : ''}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" onClick={() => handleVendorTopupAction(req.id, 'approve')}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleVendorTopupAction(req.id, 'reject')}>
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Wallet Redemption Requests (All Users) */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-col gap-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-purple-600" /> Wallet Redemption Requests
                    </CardTitle>
                    <div className="flex flex-wrap gap-2">
                      <Input placeholder="Search name..." value={redemptionFilter.search} onChange={(e) => setRedemptionFilter(f => ({...f, search: e.target.value}))} className="w-40 h-8 text-xs" data-testid="redemption-search" />
                      <Select value={redemptionFilter.status} onValueChange={(v) => setRedemptionFilter(f => ({ ...f, status: v }))}>
                        <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={redemptionFilter.user_type} onValueChange={(v) => setRedemptionFilter(f => ({ ...f, user_type: v }))}>
                        <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          <SelectItem value="doctor">Doctors</SelectItem>
                          <SelectItem value="patient">Patients</SelectItem>
                          <SelectItem value="vendor">Vendors</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="date" value={redemptionFilter.date_from || ''} onChange={(e) => setRedemptionFilter(f => ({...f, date_from: e.target.value}))} className="w-32 h-8 text-xs" />
                      <Input type="date" value={redemptionFilter.date_to || ''} onChange={(e) => setRedemptionFilter(f => ({...f, date_to: e.target.value}))} className="w-32 h-8 text-xs" />
                      <Button size="sm" variant="outline" className="h-8" onClick={fetchRedemptionRequests}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="max-h-96 overflow-y-auto space-y-3">
                  {redemptionRequests.filter(req => {
                    if (redemptionFilter.date_from && req.created_at && new Date(req.created_at).toISOString().split('T')[0] < redemptionFilter.date_from) return false;
                    if (redemptionFilter.date_to && req.created_at && new Date(req.created_at).toISOString().split('T')[0] > redemptionFilter.date_to) return false;
                    return true;
                  }).length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No redemption requests found</p>
                  ) : redemptionRequests.filter(req => {
                    if (redemptionFilter.date_from && req.created_at && new Date(req.created_at).toISOString().split('T')[0] < redemptionFilter.date_from) return false;
                    if (redemptionFilter.date_to && req.created_at && new Date(req.created_at).toISOString().split('T')[0] > redemptionFilter.date_to) return false;
                    return true;
                  }).map((req) => (
                    <div key={req.id} className="p-3 bg-white rounded-lg border shadow-sm" data-testid="redemption-admin-row">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-slate-800">{req.user_name || 'Unknown'}</p>
                            <Badge className="text-xs capitalize bg-purple-100 text-purple-600">{req.user_type}</Badge>
                            <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-700' : req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-xl font-bold text-purple-600 mb-1">₹{req.amount?.toLocaleString()}</p>
                          {req.payment_method && (
                            <div className="bg-slate-50 rounded-lg p-2 text-xs space-y-0.5">
                              <p><span className="text-slate-500">Method:</span> {req.payment_method.method_type === 'upi' ? `UPI: ${req.payment_method.upi_id}` : req.payment_method.method_type === 'bank_account' ? `Bank: ${req.payment_method.bank_name} - A/C: ${req.payment_method.account_number}` : 'QR Code'}</p>
                              {req.payment_method.account_holder_name && <p><span className="text-slate-500">Name:</span> {req.payment_method.account_holder_name}</p>}
                              {req.payment_method.ifsc_code && <p><span className="text-slate-500">IFSC:</span> {req.payment_method.ifsc_code}</p>}
                              {req.payment_method.qr_image_url && (
                                <div className="mt-1">
                                  <img src={req.payment_method.qr_image_url} alt="QR" className="w-16 h-16 border rounded" />
                                </div>
                              )}
                            </div>
                          )}
                          {req.notes && <p className="text-xs text-slate-500 mt-1">{req.notes}</p>}
                          <p className="text-xs text-slate-400 mt-1">{req.created_at ? new Date(req.created_at).toLocaleString() : ''}</p>
                        </div>
                        {req.status === 'pending' && (
                          <div className="flex flex-col gap-1">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => handleRedemptionAction(req.id, 'approve')}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleRedemptionAction(req.id, 'reject')}>
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-col gap-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" /> Transactions Ledger
                    </CardTitle>
                    <div className="flex flex-wrap gap-2">
                      <Select value={ledgerFilters.type || "all"} onValueChange={(v) => setLedgerFilters({ ...ledgerFilters, type: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-24 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="date" className="w-32 text-xs" value={ledgerFilters.date_from} onChange={(e) => setLedgerFilters({ ...ledgerFilters, date_from: e.target.value })} />
                      <Input type="date" className="w-32 text-xs" value={ledgerFilters.date_to} onChange={(e) => setLedgerFilters({ ...ledgerFilters, date_to: e.target.value })} />
                      <Input placeholder="Search..." className="w-28 text-xs flex-1 min-w-[100px]" value={ledgerFilters.search} onChange={(e) => setLedgerFilters({ ...ledgerFilters, search: e.target.value })} />
                      <Button onClick={() => fetchTransactionsLedger(1)} variant="outline" size="sm"><Search className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full text-xs sm:text-sm min-w-[600px]">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="p-2 text-left whitespace-nowrap">Date</th>
                          <th className="p-2 text-left">Doctor</th>
                          <th className="p-2 text-left">Clinic</th>
                          <th className="p-2 text-right">Amount</th>
                          <th className="p-2 text-left">UTR</th>
                          <th className="p-2 text-center">Status</th>
                          <th className="p-2 text-center">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactionsLedger.map((txn) => (
                          <tr key={txn.id} className="border-b hover:bg-slate-50">
                            <td className="p-2">{new Date(txn.created_at).toLocaleDateString()}</td>
                            <td className="p-2">{txn.doctor_name}</td>
                            <td className="p-2">{txn.clinic_name}</td>
                            <td className="p-2 text-right font-medium">₹{txn.amount?.toLocaleString()}</td>
                            <td className="p-2 text-xs">{txn.utr_number || '-'}</td>
                            <td className="p-2 text-center">{getStatusBadge(txn.status)}</td>
                            <td className="p-2 text-center">
                              {txn.screenshot_url && <a href={txn.screenshot_url} target="_blank" rel="noopener noreferrer"><Eye className="w-4 h-4 text-blue-600 mx-auto" /></a>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {ledgerTotal > 30 && (
                    <div className="flex justify-center gap-2 mt-4">
                      <Button variant="outline" size="sm" disabled={ledgerPage === 1} onClick={() => fetchTransactionsLedger(ledgerPage - 1)}>Previous</Button>
                      <span className="py-2 px-3 text-sm">Page {ledgerPage}</span>
                      <Button variant="outline" size="sm" onClick={() => fetchTransactionsLedger(ledgerPage + 1)}>Next</Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Commission Vendors */}
              {commissionData?.by_vendor && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Commission Vendors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {commissionData.by_vendor.slice(0, 8).map((v, idx) => (
                        <div key={v._id} className="p-3 bg-slate-50 rounded-lg border flex items-center gap-3 cursor-pointer hover:shadow" onClick={() => fetchVendorDetails(v._id)}>
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{v.vendor_name}</p>
                            <p className="text-xs text-slate-500 capitalize">{v.vendor_type} • {v.orders} orders</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">₹{v.commission?.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="vendor-billing">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-teal-600" /> Vendor Billing Ledger
                    </CardTitle>
                    <CardDescription>Walk-in / point-of-sale bills raised by every vendor, vendor-wise, across the platform</CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total Revenue ({vendorBillsTotal} bills)</p>
                    <p className="text-2xl font-bold text-teal-600">₹{(vendorBillsSummary.total_revenue || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="relative flex-1 min-w-[180px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search patient, invoice, vendor..." value={vendorBillFilters.search} onChange={e => setVendorBillFilters(f => ({ ...f, search: e.target.value }))} className="pl-10" data-testid="vendor-billing-search" />
                  </div>
                  <Select value={vendorBillFilters.vendor_type || 'all'} onValueChange={v => setVendorBillFilters(f => ({ ...f, vendor_type: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="Vendor Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {['chemist', 'pharma', 'lab', 'inventory', 'marketing'].map(vt => (
                        <SelectItem key={vt} value={vt}>{vt.charAt(0).toUpperCase() + vt.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="date" className="w-36" value={vendorBillFilters.date_from} onChange={e => setVendorBillFilters(f => ({ ...f, date_from: e.target.value }))} />
                  <Input type="date" className="w-36" value={vendorBillFilters.date_to} onChange={e => setVendorBillFilters(f => ({ ...f, date_to: e.target.value }))} />
                  <Select value={vendorBillFilters.sort} onValueChange={v => setVendorBillFilters(f => ({ ...f, sort: v }))}>
                    <SelectTrigger className="w-44"><SelectValue placeholder="Sort" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at:-1">Newest First</SelectItem>
                      <SelectItem value="created_at:1">Oldest First</SelectItem>
                      <SelectItem value="total:-1">Total: High to Low</SelectItem>
                      <SelectItem value="total:1">Total: Low to High</SelectItem>
                      <SelectItem value="vendor_name:1">Vendor Name (A-Z)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => fetchVendorBills(1)} data-testid="vendor-billing-apply-btn"><ArrowUpDown className="w-4 h-4 mr-1" /> Apply</Button>
                  <Button variant="ghost" onClick={() => { setVendorBillFilters({ search: '', vendor_type: '', date_from: '', date_to: '', sort: 'created_at:-1' }); fetchVendorBills(1); }}><X className="w-4 h-4 mr-1" /> Clear</Button>
                </div>

                {vendorBillsLoading ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
                ) : vendorBills.length === 0 ? (
                  <p className="text-center text-slate-400 py-10">No vendor bills found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Patient/Client</TableHead>
                        <TableHead>Ref. Doctor</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Payment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorBills.map(bill => (
                        <TableRow key={bill.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedVendorBill(bill)} data-testid={`admin-vendor-bill-${bill.id}`}>
                          <TableCell className="text-xs">{new Date(bill.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-xs font-mono">{bill.invoice_number}</TableCell>
                          <TableCell className="text-xs">{bill.vendor_name}</TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs capitalize">{bill.vendor_type || '-'}</Badge></TableCell>
                          <TableCell className="text-xs">{bill.patient_name}</TableCell>
                          <TableCell className="text-xs text-teal-700">{bill.billed_to?.clinic_name || '-'}</TableCell>
                          <TableCell className="text-right font-medium">₹{bill.total?.toFixed(2)}</TableCell>
                          <TableCell><Badge className={`text-xs ${paymentBadge(bill.payment_status).className}`}>{paymentBadge(bill.payment_status).label}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Dialog open={!!selectedVendorBill} onOpenChange={(o) => !o && setSelectedVendorBill(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Invoice: {selectedVendorBill?.invoice_number}</DialogTitle></DialogHeader>
                {selectedVendorBill && (
                  <div className="space-y-3 text-sm">
                    <div className="border-b pb-2 flex items-start gap-3">
                      {selectedVendorBill.vendor_logo && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${selectedVendorBill.vendor_logo}`} alt="Logo" className="w-12 h-12 object-contain rounded border" />
                      )}
                      <div>
                        <p className="font-bold">{selectedVendorBill.vendor_name} <Badge variant="secondary" className="text-xs capitalize">{selectedVendorBill.vendor_type}</Badge></p>
                        <p className="text-xs text-slate-500">{selectedVendorBill.vendor_address}</p>
                        <p className="text-xs text-slate-500">GST: {selectedVendorBill.vendor_gst} | Ph: {selectedVendorBill.vendor_phone}</p>
                      </div>
                    </div>
                    <div><strong>Patient/Client:</strong> {selectedVendorBill.patient_name} {selectedVendorBill.patient_phone && `| ${selectedVendorBill.patient_phone}`}</div>
                    {selectedVendorBill.billed_to && (selectedVendorBill.billed_to.gst_number || selectedVendorBill.billed_to.address || selectedVendorBill.billed_to.clinic_name) && (
                      <div className="bg-slate-50 p-2 rounded text-xs space-y-0.5">
                        <p className="font-medium">{selectedVendorBill.billed_to.clinic_name || selectedVendorBill.billed_to.name}</p>
                        {selectedVendorBill.billed_to.gst_number && <p>GSTIN: {selectedVendorBill.billed_to.gst_number}</p>}
                        {selectedVendorBill.billed_to.address && <p>{selectedVendorBill.billed_to.address}</p>}
                      </div>
                    )}
                    <div><strong>Date:</strong> {new Date(selectedVendorBill.created_at).toLocaleString()}</div>
                    <table className="w-full text-xs border"><thead><tr className="bg-slate-100"><th className="p-1 text-left">Item</th><th className="p-1">Qty</th><th className="p-1">Price</th><th className="p-1">Total</th></tr></thead><tbody>
                      {selectedVendorBill.items?.map((item, i) => (
                        <tr key={`row-${i}`} className="border-t"><td className="p-1">{item.name}</td><td className="p-1 text-center">{item.quantity}</td><td className="p-1 text-right">{item.selling_price}</td><td className="p-1 text-right">{(item.selling_price * item.quantity).toFixed(2)}</td></tr>
                      ))}
                    </tbody></table>
                    <div className="border-t pt-2 space-y-1">
                      <div className="flex justify-between"><span>Subtotal</span><span>{selectedVendorBill.subtotal?.toFixed(2)}</span></div>
                      {selectedVendorBill.discount > 0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>-{selectedVendorBill.discount?.toFixed(2)}</span></div>}
                      <div className="flex justify-between"><span>GST ({selectedVendorBill.gst_percentage}%)</span><span>{selectedVendorBill.gst_amount?.toFixed(2)}</span></div>
                      <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{selectedVendorBill.total?.toFixed(2)}</span></div>
                    </div>
                    <div className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded" data-testid="admin-vendor-bill-payment-status">
                      <span className="font-medium">Payment Status</span>
                      <div className="flex items-center gap-2">
                        <Badge className={paymentBadge(selectedVendorBill.payment_status).className}>{paymentBadge(selectedVendorBill.payment_status).label}</Badge>
                        <span className="text-slate-500">Due: {(selectedVendorBill.balance_due ?? (selectedVendorBill.total - (selectedVendorBill.amount_paid || 0))).toFixed(2)}</span>
                      </div>
                    </div>
                    {selectedVendorBill.payment_mode && (
                      <div className="text-xs text-slate-500 flex justify-between" data-testid="admin-vendor-bill-payment-mode">
                        <span>Paid via {selectedVendorBill.payment_mode.replace('_', ' ')}</span>
                        {selectedVendorBill.transaction_id && <span>Txn: {selectedVendorBill.transaction_id}</span>}
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="space-y-6">
              {/* Subscription Plans */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-600" /> Subscription Plans
                    </CardTitle>
                    <Button onClick={() => { 
                      setEditingPlan(null); 
                      setPlanForm({ 
                        name: '', tier: 'basic', billing_cycle: 'monthly', base_price: 0, description: '', 
                        features: {
                          dashboard: true, appointments: true, patients: true, clinical: true, billing: true, 
                          wallet: true, subscription: true, settings: true, support: true, patient_history: true,
                          marketplace: false, lab_access: false, pharma_access: false, inventory_access: false,
                          inventory: false, ai_scribe: false, analytics: false, expenses: false, mr_visitors: false,
                          whatsapp_reminders: false, priority_support: false, custom_branding: false
                        }
                      }); 
                      setShowPlanDialog(true); 
                    }}>
                      <Plus className="w-4 h-4 mr-1" /> Add Plan
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {subscriptionPlans.map((plan) => (
                      <div key={plan.id} className={`p-4 rounded-lg border-2 ${plan.tier === 'advance' ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50' : plan.tier === 'advance_plus' ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50' : 'border-slate-300 bg-slate-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={plan.tier === 'advance' ? 'bg-gradient-to-r from-amber-500 to-orange-600' : plan.tier === 'advance_plus' ? 'bg-gradient-to-r from-purple-500 to-indigo-600' : 'bg-slate-600'}>{plan.tier === 'advance_plus' ? 'Advance Plus' : plan.tier}</Badge>
                          <div className="flex gap-1">
                            <Badge variant="outline" className={plan.is_active ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}>
                              {plan.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => { 
                              setEditingPlan(plan); 
                              // Normalize features to ensure all toggle keys have boolean values
                              const normalizedFeatures = {
                                // Default all features
                                dashboard: false, appointments: false, patients: false, clinical: false, billing: false,
                                marketplace: false, lab_access: false, pharma_access: false, inventory_access: false,
                                inventory: false, ai_scribe: false, analytics: false, expenses: false, mr_visitors: false,
                                patient_history: false, whatsapp_reminders: false, priority_support: false, custom_branding: false,
                                // Override with actual plan features if they exist
                                ...(typeof plan.features === 'object' && !Array.isArray(plan.features) ? plan.features : {})
                              };
                              // Convert any truthy non-boolean values to true
                              Object.keys(normalizedFeatures).forEach(key => {
                                normalizedFeatures[key] = normalizedFeatures[key] === true;
                              });
                              setPlanForm({ ...plan, features: normalizedFeatures }); 
                              setShowPlanDialog(true); 
                            }}><Edit2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <h3 className="text-lg font-bold">{plan.name}</h3>
                        <p className="text-2xl font-bold text-teal-600 mt-1">₹{plan.base_price?.toLocaleString()}<span className="text-sm text-slate-500">/{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}</span></p>
                        <p className="text-sm text-slate-600 mt-2">{plan.description}</p>
                        <div className="mt-3 grid grid-cols-2 gap-1 text-xs">
                          {allFeatures.filter(f => f.category !== 'core').slice(0, 8).map(feat => (
                            <p key={feat.key} className={`flex items-center gap-1 ${plan.features?.[feat.key] ? 'text-green-700' : 'text-slate-400'}`}>
                              {plan.features?.[feat.key] ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              <span className="truncate">{feat.label}</span>
                            </p>
                          ))}
                        </div>
                        {allFeatures.filter(f => f.category !== 'core' && plan.features?.[f.key] === true).length > 8 && (
                          <p className="text-xs text-teal-600 mt-1">+{allFeatures.filter(f => f.category !== 'core' && plan.features?.[f.key] === true).length - 8} more features</p>
                        )}
                      </div>
                    ))}
                    {subscriptionPlans.length === 0 && (
                      <div className="col-span-3 text-center py-8 text-slate-500">
                        <Star className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p>No subscription plans. Create your first plan.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Promo Codes */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-pink-600" /> Promo Codes</CardTitle>
                      <Button size="sm" onClick={() => { setEditingPromo(null); setPromoForm({ code: '', discount_type: 'percentage', discount_value: 10, applicable_tiers: ['basic', 'advance', 'advance_plus'], max_uses: 0, valid_until: '', is_active: true }); setShowPromoDialog(true); }}><Plus className="w-4 h-4 mr-1" /> Add Code</Button>
                    </div>
                  </CardHeader>
                  <CardContent className="max-h-72 overflow-y-auto space-y-2">
                    {promoCodes.map((code) => (
                      <div key={code.id || code.code} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-mono font-bold text-lg">{code.code}</p>
                              <p className="text-sm text-slate-500">{code.discount_type === 'percentage' ? `${code.discount_value}% off` : `₹${code.discount_value} off`}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={code.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{code.is_active ? 'Active' : 'Inactive'}</Badge>
                            <Button variant="ghost" size="sm" onClick={() => { 
                              setEditingPromo(code); 
                              setPromoForm({ 
                                code: code.code, 
                                discount_type: code.discount_type, 
                                discount_value: code.discount_value, 
                                applicable_tiers: code.applicable_tiers || ['basic', 'advance', 'advance_plus'], 
                                max_uses: code.max_uses || 0, 
                                valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
                                is_active: code.is_active 
                              }); 
                              setShowPromoDialog(true); 
                            }}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deletePromoCode(code.id)} disabled={deletingPromoId === code.id}>
                              {deletingPromoId === code.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>Uses: {code.times_used || 0}/{code.max_uses === -1 || code.max_uses === 0 ? '∞' : code.max_uses}</span>
                          {code.valid_until && <span>Expires: {new Date(code.valid_until).toLocaleDateString()}</span>}
                          <span>Tiers: {(code.applicable_tiers || []).join(', ')}</span>
                        </div>
                      </div>
                    ))}
                    {promoCodes.length === 0 && <p className="text-center text-slate-500 py-4">No promo codes</p>}
                  </CardContent>
                </Card>

                {/* Referral Config */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Gift className="w-5 h-5 text-amber-600" /> Referral Rewards</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Reward Type</Label>
                      <Select value={referralForm.reward_type} onValueChange={(v) => setReferralForm({ ...referralForm, reward_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Flat Points</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Reward Value ({referralForm.reward_type === 'flat' ? '₹' : '%'})</Label>
                      <Input type="number" value={referralForm.reward_value} onChange={(e) => setReferralForm({ ...referralForm, reward_value: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label>Min First Transaction (₹)</Label>
                      <Input type="number" value={referralForm.min_first_transaction} onChange={(e) => setReferralForm({ ...referralForm, min_first_transaction: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <Button onClick={saveReferralConfig} disabled={savingReferral} className="w-full">
                      {savingReferral ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Config
                    </Button>
                    {referralConfig && (
                      <div className="p-3 bg-amber-50 rounded-lg text-sm">
                        <p className="font-medium">Current: {referralConfig.reward_type === 'flat' ? `₹${referralConfig.reward_value}` : `${referralConfig.reward_value}%`} per referral</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Subscription Offers */}
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Zap className="w-5 h-5 text-orange-500" /> Subscription Offers
                    </CardTitle>
                    <Button size="sm" onClick={() => { 
                      setEditingOffer(null); 
                      setOfferForm({ title: '', description: '', offer_type: 'free_trial', duration_days: 30, discount_value: 0, tier: 'advance_plus', max_claims: 0 }); 
                      setShowOfferDialog(true); 
                    }} data-testid="add-offer-btn">
                      <Plus className="w-4 h-4 mr-1" /> Create Offer
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {offers.length === 0 ? (
                    <p className="text-center text-slate-500 py-6">No offers created yet. Create your first offer!</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {offers.map(offer => (
                        <div key={offer.id} className={`p-4 rounded-xl border-2 ${offer.is_active ? 'border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50' : 'border-slate-200 bg-slate-50 opacity-60'}`} data-testid={`offer-card-${offer.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <Badge className={offer.offer_type === 'free_trial' ? 'bg-green-600' : offer.offer_type === 'percentage' ? 'bg-blue-600' : 'bg-purple-600'}>
                              {offer.offer_type === 'free_trial' ? 'Free Trial' : offer.offer_type === 'percentage' ? `${offer.discount_value}% Off` : `₹${offer.discount_value} Off`}
                            </Badge>
                            <div className="flex gap-1">
                              <Switch checked={offer.is_active} onCheckedChange={() => toggleOfferActive(offer)} />
                            </div>
                          </div>
                          <h3 className="font-bold text-slate-800">{offer.title}</h3>
                          <p className="text-xs text-slate-500 mt-1">{offer.description || 'No description'}</p>
                          <div className="flex items-center gap-3 mt-3 text-xs text-slate-600">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {offer.duration_days} days</span>
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {offer.current_claims}/{offer.max_claims || '∞'} claims</span>
                            <Badge variant="outline" className="text-[10px]">{offer.tier}</Badge>
                          </div>
                          <div className="flex gap-1 mt-3">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => {
                              setEditingOffer(offer);
                              setOfferForm({ title: offer.title, description: offer.description || '', offer_type: offer.offer_type, duration_days: offer.duration_days, discount_value: offer.discount_value || 0, tier: offer.tier || 'advance_plus', max_claims: offer.max_claims || 0 });
                              setShowOfferDialog(true);
                            }}>
                              <Edit2 className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => deleteOffer(offer.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Offer Claims (Doctors who opted for offers) */}
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-orange-500" /> Offer Claims
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchOfferClaims} data-testid="refresh-offer-claims">
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingOfferClaims ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                  ) : offerClaims.length === 0 ? (
                    <p className="text-center text-slate-500 py-6">No doctors have claimed any offers yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Doctor</TableHead>
                          <TableHead>Offer</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Started</TableHead>
                          <TableHead>Expires</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {offerClaims.map(claim => (
                          <TableRow key={claim.id} data-testid={`offer-claim-${claim.id}`}>
                            <TableCell>
                              <p className="font-medium">{claim.doctor_name}</p>
                              <p className="text-xs text-slate-400">{claim.clinic_name}</p>
                            </TableCell>
                            <TableCell className="font-medium">{claim.offer_title || claim.plan_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {claim.offer_type === 'free_trial' ? 'Free Trial' : claim.offer_type === 'percentage' ? '% Discount' : 'Flat Discount'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={claim.status === 'active' ? 'bg-green-100 text-green-700' : claim.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>
                                {claim.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{claim.starts_at ? new Date(claim.starts_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                            <TableCell className="text-xs">{claim.expires_at ? new Date(claim.expires_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Referrals List */}
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Gift className="w-5 h-5 text-amber-600" /> Referrals
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input 
                        placeholder="Search referrer/referred..." 
                        value={referralsFilters.search} 
                        onChange={(e) => setReferralsFilters({ ...referralsFilters, search: e.target.value })}
                        className="w-48"
                      />
                      <Select value={referralsFilters.status || "all"} onValueChange={(v) => setReferralsFilters({ ...referralsFilters, status: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="credited">Credited</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={referralsFilters.user_type || "all"} onValueChange={(v) => setReferralsFilters({ ...referralsFilters, user_type: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-32"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="doctor">Doctors</SelectItem>
                          <SelectItem value="vendor">Vendors</SelectItem>
                          <SelectItem value="patient">Patients</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={fetchReferralsList}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {referralsLoading ? (
                    <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-medium">Referrer</th>
                            <th className="text-left p-3 font-medium">Referred</th>
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Date & Time</th>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-right p-3 font-medium">Reward</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referralsList.map((ref) => (
                            <tr key={ref.id} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                <div className="font-medium text-teal-700">{ref.referrer_name || 'N/A'}</div>
                                <div className="text-xs text-slate-500">{ref.referrer_email}</div>
                                <div className="text-xs text-slate-500">{ref.referrer_phone}</div>
                                {ref.referrer_clinic && <div className="text-xs text-slate-400">{ref.referrer_clinic}</div>}
                              </td>
                              <td className="p-3">
                                <div className="font-medium">{ref.referred_name || 'N/A'}</div>
                                <div className="text-xs text-slate-500">{ref.referred_email}</div>
                                <div className="text-xs text-slate-500">{ref.referred_phone}</div>
                                {ref.referred_clinic && <div className="text-xs text-slate-400">{ref.referred_clinic}</div>}
                              </td>
                              <td className="p-3">
                                <Badge className={
                                  ref.referral_type === 'patient' ? 'bg-green-100 text-green-700' :
                                  ref.referred_type === 'vendor' ? 'bg-purple-100 text-purple-700' : 
                                  'bg-blue-100 text-blue-700'
                                }>
                                  {ref.referral_type === 'patient' ? 'Patient' :
                                   ref.referred_type === 'vendor' ? `Vendor (${ref.referred_vendor_type || ''})` : 'Doctor'}
                                </Badge>
                              </td>
                              <td className="p-3 text-sm">
                                <div>{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '-'}</div>
                                <div className="text-xs text-slate-500">{ref.created_at ? new Date(ref.created_at).toLocaleTimeString() : ''}</div>
                              </td>
                              <td className="p-3">
                                {ref.status === 'credited' ? (
                                  <div>
                                    <Badge className="bg-green-100 text-green-700">Credited</Badge>
                                    {ref.credited_at && <div className="text-xs text-slate-500 mt-1">{new Date(ref.credited_at).toLocaleDateString()}</div>}
                                  </div>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                {ref.reward_amount ? (
                                  <span className="font-medium text-green-600">₹{ref.reward_amount}</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {referralsList.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center py-8 text-slate-500">No referrals found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {referralsTotal > 20 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-slate-500">Showing {referralsList.length} of {referralsTotal}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={referralsPage === 1} onClick={() => setReferralsPage(p => p - 1)}>Previous</Button>
                        <Button variant="outline" size="sm" disabled={referralsList.length < 20} onClick={() => setReferralsPage(p => p + 1)}>Next</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Doctor Subscriptions List */}
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-teal-600" /> Doctor Subscriptions
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input 
                        placeholder="Search by name, email, phone..." 
                        value={doctorSubsFilters.search} 
                        onChange={(e) => setDoctorSubsFilters({ ...doctorSubsFilters, search: e.target.value })}
                        className="w-52"
                      />
                      <Select value={doctorSubsFilters.subscription_status || "all"} onValueChange={(v) => setDoctorSubsFilters({ ...doctorSubsFilters, subscription_status: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="subscribed">Subscribed</SelectItem>
                          <SelectItem value="not_subscribed">Not Subscribed</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={doctorSubsFilters.tier || "all"} onValueChange={(v) => setDoctorSubsFilters({ ...doctorSubsFilters, tier: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-32"><SelectValue placeholder="All Tiers" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tiers</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="advance">Advance</SelectItem>
                          <SelectItem value="advance_plus">Advance Plus</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={fetchDoctorSubscriptions}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedDoctorIds.length > 0 && (
                    <div className="mb-3 flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg" data-testid="doctor-bulk-bar">
                      <span className="text-sm font-medium text-red-700">{selectedDoctorIds.length} selected</span>
                      <Button variant="destructive" size="sm" onClick={() => setDeleteConfirm({ type: 'doctor', ids: selectedDoctorIds, names: doctorSubscriptions.filter(d => selectedDoctorIds.includes(d.id || d.doctor_id)).map(d => d.name) })} data-testid="bulk-delete-doctors-btn">
                        <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
                      </Button>
                    </div>
                  )}
                  {doctorSubsLoading ? (
                    <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 w-8">
                              <input type="checkbox" checked={doctorSubscriptions.length > 0 && selectedDoctorIds.length === doctorSubscriptions.length} onChange={() => setSelectedDoctorIds(prev => prev.length === doctorSubscriptions.length ? [] : doctorSubscriptions.map(d => d.id || d.doctor_id))} data-testid="select-all-doctors-checkbox" />
                            </th>
                            <th className="text-left p-3 font-medium">Doctor</th>
                            <th className="text-left p-3 font-medium">Contact</th>
                            <th className="text-left p-3 font-medium">Clinic</th>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-left p-3 font-medium">Plan</th>
                            <th className="text-left p-3 font-medium">Expiry</th>
                            <th className="text-right p-3 font-medium">Wallet</th>
                            <th className="text-right p-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorSubscriptions.map((doc) => (
                            <tr key={doc._id || doc.id} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                <input type="checkbox" checked={selectedDoctorIds.includes(doc.id || doc.doctor_id)} onChange={() => setSelectedDoctorIds(prev => prev.includes(doc.id || doc.doctor_id) ? prev.filter(x => x !== (doc.id || doc.doctor_id)) : [...prev, doc.id || doc.doctor_id])} data-testid={`select-doctor-${doc.id || doc.doctor_id}`} />
                              </td>
                              <td className="p-3">
                                <div className="font-medium">{doc.name || 'N/A'}</div>
                              </td>
                              <td className="p-3">
                                <div className="text-xs">{doc.email}</div>
                                <div className="text-xs text-slate-500">{doc.phone || '-'}</div>
                              </td>
                              <td className="p-3 text-sm">{doc.clinic_name || '-'}</td>
                              <td className="p-3">
                                {doc.subscription_status === 'subscribed' && (
                                  <Badge className="bg-green-100 text-green-700">Subscribed</Badge>
                                )}
                                {doc.subscription_status === 'not_subscribed' && (
                                  <Badge className="bg-slate-100 text-slate-600">Not Subscribed</Badge>
                                )}
                                {doc.subscription_status === 'expired' && (
                                  <Badge className="bg-red-100 text-red-700">Expired</Badge>
                                )}
                                {doc.subscription?.status === 'suspended' && (
                                  <Badge className="bg-amber-100 text-amber-700">Suspended</Badge>
                                )}
                              </td>
                              <td className="p-3">
                                {doc.subscription ? (
                                  <div>
                                    <Badge className={doc.subscription.tier === 'advance' ? 'bg-amber-100 text-amber-700' : doc.subscription.tier === 'advance_plus' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}>
                                      {doc.subscription.tier?.toUpperCase()}
                                    </Badge>
                                    <div className="text-xs text-slate-500 mt-1">{doc.subscription.plan_name}</div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                {doc.subscription?.expires_at ? (
                                  <span className={new Date(doc.subscription.expires_at) < new Date() ? 'text-red-600' : ''}>
                                    {new Date(doc.subscription.expires_at).toLocaleDateString()}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="p-3 text-right font-medium">₹{doc.wallet_balance?.toLocaleString() || 0}</td>
                              <td className="p-3 text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`doctor-actions-${doc.id || doc.doctor_id}`}>
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => { setEditDoctorDialog(doc); setEditDoctorForm({ name: doc.name || '', email: doc.email || '', phone: doc.phone || '', clinic_name: doc.clinic_name || '', specialization: doc.specialization || '', registration_number: doc.registration_number || '' }); }}>
                                      <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {doc.subscription_status === 'subscribed' && (
                                      <DropdownMenuItem onClick={() => handleDoctorSubAction(doc.id || doc.doctor_id, 'suspend')} className="text-amber-600">
                                        <PauseCircle className="w-4 h-4 mr-2" /> Suspend Subscription
                                      </DropdownMenuItem>
                                    )}
                                    {doc.subscription?.status === 'suspended' && (
                                      <DropdownMenuItem onClick={() => handleDoctorSubAction(doc.id || doc.doctor_id, 'reactivate')} className="text-green-600">
                                        <PlayCircle className="w-4 h-4 mr-2" /> Reactivate Subscription
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => setDeleteConfirm({ type: 'doctor', ids: [doc.id || doc.doctor_id], names: [doc.name] })} className="text-red-600">
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete Account
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          ))}
                          {doctorSubscriptions.length === 0 && (
                            <tr>
                              <td colSpan="9" className="text-center py-8 text-slate-500">No doctors found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {doctorSubsTotal > 20 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-slate-500">Showing {doctorSubscriptions.length} of {doctorSubsTotal}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={doctorSubsPage === 1} onClick={() => setDoctorSubsPage(p => p - 1)}>Previous</Button>
                        <Button variant="outline" size="sm" disabled={doctorSubscriptions.length < 20} onClick={() => setDoctorSubsPage(p => p + 1)}>Next</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
            <DialogDescription>Configure subscription plan features that will be available to doctors</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan Name</Label>
                <Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="e.g., Basic Monthly" />
              </div>
              <div>
                <Label>Price (₹)</Label>
                <Input type="number" value={planForm.base_price} onChange={(e) => setPlanForm({ ...planForm, base_price: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tier</Label>
                <Select value={planForm.tier} onValueChange={(v) => setPlanForm({ ...planForm, tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="advance">Advance</SelectItem>
                    <SelectItem value="advance_plus">Advance Plus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing Cycle</Label>
                <Select value={planForm.billing_cycle} onValueChange={(v) => setPlanForm({ ...planForm, billing_cycle: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} rows={2} placeholder="Brief description of the plan" />
            </div>
            
            {/* Feature Toggles - Organized by Category */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Feature Access Control</Label>
              <p className="text-sm text-slate-500 mb-4">Toggle features that doctors can access with this plan. Wallet, Subscription, Settings, and Support are always available.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Core Features */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Core Features</p>
                  {allFeatures.filter(f => f.category === 'core').map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <span className="text-sm">{feat.label}</span>
                      <Switch 
                        checked={planForm.features?.[feat.key] || false} 
                        onCheckedChange={(v) => setPlanForm({ ...planForm, features: { ...planForm.features, [feat.key]: v } })} 
                      />
                    </div>
                  ))}
                </div>
                
                {/* Business Features */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">B2B & Marketplace</p>
                  {allFeatures.filter(f => f.category === 'business').map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <span className="text-sm">{feat.label}</span>
                      <Switch 
                        checked={planForm.features?.[feat.key] || false} 
                        onCheckedChange={(v) => setPlanForm({ ...planForm, features: { ...planForm.features, [feat.key]: v } })} 
                      />
                    </div>
                  ))}
                </div>
                
                {/* Clinic Features */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Clinic Management</p>
                  {allFeatures.filter(f => f.category === 'clinic').map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <span className="text-sm">{feat.label}</span>
                      <Switch 
                        checked={planForm.features?.[feat.key] || false} 
                        onCheckedChange={(v) => setPlanForm({ ...planForm, features: { ...planForm.features, [feat.key]: v } })} 
                      />
                    </div>
                  ))}
                </div>
                
                {/* AI & Communication */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">AI & Communication</p>
                  {allFeatures.filter(f => f.category === 'ai' || f.category === 'communication').map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <span className="text-sm">{feat.label}</span>
                      <Switch 
                        checked={planForm.features?.[feat.key] || false} 
                        onCheckedChange={(v) => setPlanForm({ ...planForm, features: { ...planForm.features, [feat.key]: v } })} 
                      />
                    </div>
                  ))}
                </div>
                
                {/* Support */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Support</p>
                  {allFeatures.filter(f => f.category === 'support').map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <span className="text-sm">{feat.label}</span>
                      <Switch 
                        checked={planForm.features?.[feat.key] || false} 
                        onCheckedChange={(v) => setPlanForm({ ...planForm, features: { ...planForm.features, [feat.key]: v } })} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Plan Status */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <p className="font-medium">Plan Active</p>
                <p className="text-sm text-slate-500">Inactive plans won't be shown to doctors</p>
              </div>
              <Switch 
                checked={planForm.is_active !== false} 
                onCheckedChange={(v) => setPlanForm({ ...planForm, is_active: v })} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
            <Button onClick={savePlan} disabled={savingPlan}>
              {savingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promo Code Dialog */}
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle>
            <DialogDescription>
              {editingPromo ? 'Update the promo code details' : 'Create a new discount promo code for subscriptions'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label>Code</Label>
              <Input value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} placeholder="e.g., WELCOME20" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount Type</Label>
                <Select value={promoForm.discount_type} onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="flat">Flat Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value ({promoForm.discount_type === 'percentage' ? '%' : '₹'})</Label>
                <Input type="number" value={promoForm.discount_value} onChange={(e) => setPromoForm({ ...promoForm, discount_value: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max Uses (0 = unlimited)</Label>
                <Input type="number" value={promoForm.max_uses} onChange={(e) => setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Expiry Date (optional)</Label>
                <Input type="date" value={promoForm.valid_until} onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Applicable Tiers</Label>
              <div className="flex gap-3">
                {[...new Set(subscriptionPlans.map(p => p.tier).filter(Boolean))].map((tier) => (
                  <label key={tier} className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={promoForm.applicable_tiers?.includes(tier)} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPromoForm({ ...promoForm, applicable_tiers: [...(promoForm.applicable_tiers || []), tier] });
                        } else {
                          setPromoForm({ ...promoForm, applicable_tiers: (promoForm.applicable_tiers || []).filter(t => t !== tier) });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="capitalize">{tier}</span>
                  </label>
                ))}
              </div>
            </div>
            {editingPromo && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Promo Active</p>
                  <p className="text-sm text-slate-500">Inactive codes can't be used</p>
                </div>
                <Switch 
                  checked={promoForm.is_active} 
                  onCheckedChange={(v) => setPromoForm({ ...promoForm, is_active: v })} 
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPromoDialog(false); setEditingPromo(null); }}>Cancel</Button>
            <Button onClick={savePromoCode} disabled={savingPromo}>
              {savingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} 
              {editingPromo ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle>{editingOffer ? 'Edit Offer' : 'Create Subscription Offer'}</DialogTitle>
            <DialogDescription>
              {editingOffer ? 'Update offer details' : 'Create a free trial, discount, or promotional offer for doctors'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label>Offer Title</Label>
              <Input value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} placeholder="e.g., Free 1-Month Trial" data-testid="offer-title-input" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={offerForm.description} onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })} placeholder="Get all premium features free..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Offer Type</Label>
                <Select value={offerForm.offer_type} onValueChange={(v) => setOfferForm({ ...offerForm, offer_type: v })}>
                  <SelectTrigger data-testid="offer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free_trial">Free Trial</SelectItem>
                    <SelectItem value="percentage">Percentage Discount</SelectItem>
                    <SelectItem value="flat">Flat Amount Discount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (days)</Label>
                <Input type="number" value={offerForm.duration_days} onChange={(e) => setOfferForm({ ...offerForm, duration_days: parseInt(e.target.value) || 30 })} data-testid="offer-duration-input" />
              </div>
            </div>
            {offerForm.offer_type !== 'free_trial' && (
              <div>
                <Label>Discount Value ({offerForm.offer_type === 'percentage' ? '%' : '₹'})</Label>
                <Input type="number" value={offerForm.discount_value} onChange={(e) => setOfferForm({ ...offerForm, discount_value: parseFloat(e.target.value) || 0 })} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Features Tier</Label>
                <Select value={offerForm.tier} onValueChange={(v) => setOfferForm({ ...offerForm, tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="advance">Advance</SelectItem>
                    <SelectItem value="advance_plus">Advance Plus (All)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Claims (0 = unlimited)</Label>
                <Input type="number" value={offerForm.max_claims} onChange={(e) => setOfferForm({ ...offerForm, max_claims: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              {offerForm.offer_type === 'free_trial' 
                ? `Doctors will get all features for ${offerForm.duration_days} days at ₹0. Features expire immediately after the trial ends.`
                : `Doctors will get ${offerForm.offer_type === 'percentage' ? offerForm.discount_value + '% off' : '₹' + offerForm.discount_value + ' off'} for ${offerForm.duration_days} days.`}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowOfferDialog(false); setEditingOffer(null); }}>Cancel</Button>
            <Button onClick={saveOffer} disabled={savingOffer} data-testid="save-offer-btn">
              {savingOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {editingOffer ? 'Update' : 'Create Offer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Details Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Vendor Details</DialogTitle>
          </DialogHeader>
          {vendorDetails && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-bold text-lg">{vendorDetails.vendor?.name}</p>
                <p className="text-sm text-slate-500 capitalize">{vendorDetails.vendor?.vendor_type}</p>
                <p className="text-sm">{vendorDetails.vendor?.email} • {vendorDetails.vendor?.phone}</p>
              </div>
              
              {/* Earnings */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-green-600">Total Earnings</p>
                  <p className="font-bold text-green-700">₹{vendorDetails.earnings_summary?.total_payout?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-xs text-amber-600">Pending Balance</p>
                  <p className="font-bold text-amber-700">₹{vendorDetails.earnings_summary?.pending_balance?.toLocaleString() || 0}</p>
                </div>
              </div>
              
              {/* Bank Accounts */}
              {vendorDetails.bank_accounts?.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-2">Bank Accounts</p>
                  {vendorDetails.bank_accounts.map((acc) => (
                    <div key={acc.id} className="p-2 bg-slate-50 rounded text-sm">
                      <p className="font-medium">{acc.bank_name}</p>
                      <p className="text-slate-500">{acc.account_number_masked} • {acc.ifsc_code}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* UPI */}
              {vendorDetails.vendor?.upi_id && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600">UPI Details</p>
                  <p className="font-medium">{vendorDetails.vendor.upi_id}</p>
                  {vendorDetails.vendor.upi_qr_url && <img src={vendorDetails.vendor.upi_qr_url} alt="UPI QR" className="w-24 h-24 mt-2 rounded" />}
                </div>
              )}
              
              {/* Commission Config */}
              <div>
                <p className="font-medium text-sm mb-2">Commission Rate</p>
                <div className="flex gap-2">
                  <Select value={vendorCommissionForm.commission_type} onValueChange={(v) => setVendorCommissionForm({ ...vendorCommissionForm, commission_type: v })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="flat">Flat Fee</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={vendorCommissionForm.commission_value} onChange={(e) => setVendorCommissionForm({ ...vendorCommissionForm, commission_value: parseFloat(e.target.value) || 0 })} className="w-24" />
                  <span className="py-2">{vendorCommissionForm.commission_type === 'percentage' ? '%' : '₹'}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Close</Button>
            <Button onClick={saveVendorCommission} disabled={savingVendorCommission}>
              {savingVendorCommission ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Commission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={showAddCreditsDialog} onOpenChange={setShowAddCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Credits to Doctor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label>Select Doctor</Label>
              <Select value={addCreditsForm.doctor_id} onValueChange={(v) => setAddCreditsForm({ ...addCreditsForm, doctor_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select doctor..." /></SelectTrigger>
                <SelectContent>
                  {doctorsList.filter(doc => doc.doctor_id && doc.doctor_id.trim() !== '').map((doc) => (
                    <SelectItem key={doc.doctor_id} value={doc.doctor_id}>
                      {doc.doctor_name || 'Unknown'} ({doc.clinic_name || 'No clinic'}) - ₹{doc.balance || 0}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" value={addCreditsForm.amount} onChange={(e) => setAddCreditsForm({ ...addCreditsForm, amount: e.target.value })} placeholder="Enter amount" />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea value={addCreditsForm.reason} onChange={(e) => setAddCreditsForm({ ...addCreditsForm, reason: e.target.value })} placeholder="e.g., Promotional credit, Refund, etc." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCreditsDialog(false)}>Cancel</Button>
            <Button onClick={addCreditsToDoctor} disabled={addingCredits}>
              {addingCredits ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doctor-Vendor Commission Dialog */}
      <Dialog open={showDoctorCommissionDialog} onOpenChange={setShowDoctorCommissionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Doctor-Vendor Commission Rate</DialogTitle>
            <DialogDescription>
              Set a custom commission percentage for this doctor-vendor pair. This will override the global default ({globalDefaultCommission}%).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Doctor</Label>
              <Select 
                value={doctorCommissionForm.doctor_id} 
                onValueChange={(v) => setDoctorCommissionForm(prev => ({ ...prev, doctor_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a doctor..." />
                </SelectTrigger>
                <SelectContent>
                  {allDoctorsList.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.clinic_name || doctor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Vendor</Label>
              <Select 
                value={doctorCommissionForm.vendor_id} 
                onValueChange={(v) => setDoctorCommissionForm(prev => ({ ...prev, vendor_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {allVendors.filter(v => v.status === 'approved').map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      <div className="flex items-center gap-2">
                        {getVendorIcon(vendor.vendor_type)}
                        <span>{vendor.name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">{vendor.vendor_type}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commission Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={doctorCommissionForm.commission_percent}
                  onChange={(e) => setDoctorCommissionForm(prev => ({ ...prev, commission_percent: parseFloat(e.target.value) || 0 }))}
                  className="w-24"
                />
                <span className="text-slate-500">%</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Doctor will earn {doctorCommissionForm.commission_percent}% on patient orders from this vendor.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDoctorCommissionDialog(false)}>Cancel</Button>
            <Button onClick={saveDoctorVendorCommission} disabled={savingDoctorCommission}>
              {savingDoctorCommission ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Commission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Logout
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You will need to log in again to access the admin dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelLogout}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmLogout} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Support Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span className="truncate">{selectedTicket?.subject}</span>
              <Badge variant={selectedTicket?.status === 'open' ? 'default' : selectedTicket?.status === 'resolved' ? 'outline' : 'secondary'} className="text-xs">
                {selectedTicket?.status?.replace('_', ' ')}
              </Badge>
            </DialogTitle>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>{selectedTicket?.ticket_number} | {selectedTicket?.user_name} ({selectedTicket?.user_type}) | {selectedTicket?.user_email}</p>
              <p>Created: {selectedTicket?.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}</p>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Badge variant="secondary">{selectedTicket?.category}</Badge>
              <Badge className={`${selectedTicket?.priority === 'urgent' ? 'bg-red-100 text-red-700' : selectedTicket?.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>
                {selectedTicket?.priority}
              </Badge>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm">{selectedTicket?.message}</p>
            </div>
            {selectedTicket?.responses?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Conversation</p>
                {selectedTicket.responses.map((r, i) => (
                  <div key={r.id || i} className={`p-3 rounded-lg text-sm ${r.is_admin ? 'bg-teal-50 border-l-2 border-teal-500' : 'bg-slate-50'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-xs">{r.user_name || (r.is_admin ? '1Clin Support' : selectedTicket?.user_name)}</span>
                      <span className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p>{r.message}</p>
                  </div>
                ))}
              </div>
            )}
            {selectedTicket?.status !== 'closed' && (
              <div className="space-y-3 border-t pt-3">
                <Textarea
                  data-testid="admin-reply-text"
                  value={adminReplyText}
                  onChange={e => setAdminReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                />
                <div className="flex items-center gap-3">
                  <Select value={adminReplyStatus} onValueChange={setAdminReplyStatus}>
                    <SelectTrigger className="w-[160px] h-9" data-testid="admin-reply-status">
                      <SelectValue placeholder="Change status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    className="ml-auto"
                    onClick={() => handleAdminReply(selectedTicket?.id)}
                    disabled={sendingReply || !adminReplyText.trim()}
                    data-testid="admin-reply-send-btn"
                  >
                    {sendingReply ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Reply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Doctor Dialog */}
      <Dialog open={!!editDoctorDialog} onOpenChange={(open) => !open && setEditDoctorDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Doctor Profile</DialogTitle>
            <DialogDescription>Update doctor information</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={editDoctorForm.name || ''} onChange={(e) => setEditDoctorForm({...editDoctorForm, name: e.target.value})} /></div>
            <div><Label>Email</Label><Input value={editDoctorForm.email || ''} onChange={(e) => setEditDoctorForm({...editDoctorForm, email: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={editDoctorForm.phone || ''} onChange={(e) => setEditDoctorForm({...editDoctorForm, phone: e.target.value})} /></div>
            <div><Label>Clinic Name</Label><Input value={editDoctorForm.clinic_name || ''} onChange={(e) => setEditDoctorForm({...editDoctorForm, clinic_name: e.target.value})} /></div>
            <div><Label>Specialization</Label><Input value={editDoctorForm.specialization || ''} onChange={(e) => setEditDoctorForm({...editDoctorForm, specialization: e.target.value})} /></div>
            <div><Label>Registration No.</Label><Input value={editDoctorForm.registration_number || ''} onChange={(e) => setEditDoctorForm({...editDoctorForm, registration_number: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDoctorDialog(null)}>Cancel</Button>
            <Button onClick={handleEditDoctorSave} data-testid="save-doctor-profile">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Patient Dialog */}
      <Dialog open={!!editPatientDialog} onOpenChange={(open) => !open && setEditPatientDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Patient Profile</DialogTitle>
            <DialogDescription>Update patient information</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={editPatientForm.name || ''} onChange={(e) => setEditPatientForm({...editPatientForm, name: e.target.value})} /></div>
            <div><Label>Email</Label><Input value={editPatientForm.email || ''} onChange={(e) => setEditPatientForm({...editPatientForm, email: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={editPatientForm.phone || ''} onChange={(e) => setEditPatientForm({...editPatientForm, phone: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Gender</Label>
                <Select value={editPatientForm.gender || ''} onValueChange={(v) => setEditPatientForm({...editPatientForm, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Blood Group</Label><Input value={editPatientForm.blood_group || ''} onChange={(e) => setEditPatientForm({...editPatientForm, blood_group: e.target.value})} placeholder="e.g. O+" /></div>
            </div>
            <div><Label>Date of Birth</Label><Input type="date" value={editPatientForm.date_of_birth || ''} onChange={(e) => setEditPatientForm({...editPatientForm, date_of_birth: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPatientDialog(null)}>Cancel</Button>
            <Button onClick={handleEditPatientSave} data-testid="save-patient-profile">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - Step 1 */}
      <AlertDialog open={!!deleteConfirm && !mpinDialogOpen} onOpenChange={(open) => {
        if (!open) {
          if (proceedingToStep2Ref.current) {
            proceedingToStep2Ref.current = false;
          } else {
            setDeleteConfirm(null);
          }
        }
      }}>
        <AlertDialogContent data-testid="delete-confirm-step1">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteConfirm?.ids?.length > 1 ? `${deleteConfirm.ids.length} ${deleteConfirm?.type === 'doctor' ? 'Doctors' : deleteConfirm?.type === 'vendor' ? 'Vendors' : 'Patients'}` : deleteConfirm?.type === 'doctor' ? 'Doctor' : deleteConfirm?.type === 'vendor' ? 'Vendor' : 'Patient'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              {deleteConfirm?.ids?.length > 1 ? (
                <strong>{deleteConfirm.ids.length} accounts</strong>
              ) : (
                <strong>{deleteConfirm?.names?.[0]}</strong>
              )}{' '}
              and ALL associated data across every portal (orders, wallets, appointments, reports, notifications, etc). This action cannot be undone. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-step1-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAccount} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account">
              Yes, Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation - Step 2: MPIN re-entry */}
      <Dialog open={mpinDialogOpen} onOpenChange={(open) => { if (!open) { setMpinDialogOpen(false); setDeleteConfirm(null); setDeleteMpinValue(''); } }}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-step2-mpin">
          <DialogHeader>
            <DialogTitle>Enter Your MPIN to Confirm</DialogTitle>
            <DialogDescription>
              For security, re-enter your admin MPIN to permanently delete{' '}
              {deleteConfirm?.ids?.length > 1 ? `these ${deleteConfirm.ids.length} accounts` : deleteConfirm?.names?.[0]}.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            placeholder="Enter 4-digit MPIN"
            value={deleteMpinValue}
            onChange={(e) => setDeleteMpinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={(e) => e.key === 'Enter' && confirmDeleteWithMpin()}
            autoFocus
            data-testid="delete-mpin-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMpinDialogOpen(false); setDeleteConfirm(null); setDeleteMpinValue(''); }} data-testid="delete-mpin-cancel">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteWithMpin}
              disabled={deletingAccount || deleteMpinValue.length !== 4}
              data-testid="delete-mpin-confirm"
            >
              {deletingAccount ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
